
import { Component, Vue, Watch, Prop } from "vue-property-decorator";
import { EventHandler } from "@/modules/events";
import { MyErrorHandler } from "@/modules/errors";
import HeaderTitle from "@/components/HeaderTitle.vue";
import TableReport from "@/components/TableReport.vue";
import BigNumberReport from "@/components/BigNumberReport.vue";
import DateFilter from "@/components/DateFilter.vue";
import GroupByDimension from "@/components/GroupByDimension.vue";
import AnalyticsTypeFilter from "@/components/AnalyticsTypeFilter.vue";
import ChatWithUs from "@/components/ChatWithUs.vue";
import { MeasureModel } from "@/models/measure";
import { DimensionModel } from "@/models/dimension";
import { AnalyticsTypeModel } from "@/models/analytics_type";
import { CustomReportModel } from "@/models/custom_report";
import { TemplateReportModel } from "@/models/template_report";
import dateFormat from "dateformat";
import { FilterDimensionList } from "@/collections/filter_dimensions";
import { StoreModel } from "@/models/store";
import { TemplateReportList } from "@/collections/template_reports";
import { MeasureList } from "@/collections/measures";
import FilterColumn from "@/components/FilterColumn.vue";
import ReportParam from "@/components/ReportParam.vue";
import { FilterColumnList } from "@/collections/filter_columns";
import { ParamList } from "@/collections/params";
import { FilterColumnModel } from "@/models/filter_column";
import { CustomReportList } from "@/collections/custom_reports";
import FeatureLockedNotify from "@/components/FeatureLockedNotify.vue";
import { postUnlockReviewService } from '@/services/PostUnlockReviewService';
import { RuleModel } from "@/models/rule";
import ColumnSelector from "@/components/ColumnSelector.vue";
import CardColumnSelector from "@/components/CardColumnSelector.vue";
import ReportCard from "../components/ReportCard.vue";
import { ReportCardModel } from "@/models/report_card";
import { ParamModel } from "@/models/param";
import { SearchModel } from "@/models/search";
import ErrorMessage from "@/components/ErrorMessage.vue";
import ComparisonDate from "@/components/ComparisonDate.vue";
import { ComparisonDateModel } from "@/models/comparison_date";
import { EventBus } from "@/main";
import Alert from "@/components/Alert.vue";
import ActionButton from "@/components/ActionButton.vue";
import LoadingSlider from "@/components/LoadingSlider.vue";
import ReportAssistantDialog from "@/components/ReportAssistantDialog.vue";
import { DimensionColumnList } from "@/collections/dimension_columns";
import { MetricList } from "@/collections/metrics";
import SegmentSelector from "@/components/SegmentSelector.vue";
import { ProductSegmentModel } from "@/models/product_segment";
import EtlStatusPage from "@/components/EtlStatusPage.vue";
import { DashboardList } from "@/collections/dashboards";
import { DashboardModel } from "@/models/dashboard";
import { ScheduleModel } from "@/models/schedule";
import { ScheduleProfileModel } from "@/models/schedule_profile";
import { ScheduleProfileList } from "@/collections/schedule_profiles";
import moment from "moment";
import ReportSearch from "@/components/ReportSearch.vue";
import { FunnelEventModel } from '@/models/funnelEvent';
import OnboardCompleteConfirm from "@/components/OnboardCompleteConfirm.vue";
import AppcueTrigger from '@/components/AppcueTrigger.vue';
import StartupOffer from '@/components/StartupOffer.vue';
import DataSyncStatusBanner from '@/components/DataSyncStatusBanner.vue';
import { init } from "mixpanel-browser";
import zlib from 'zlib';
import { Buffer } from 'buffer';
import ColorRuleEditor from './ColorRuleEditor.vue'
//import { useColorRules } from '@/composables/useColorRules'
import type { ColorRule } from '@/models/color_rule'
import { ColorRuleList } from "@/collections/color_rules";
import ReportScheduleManager from '@/components/schedule/ReportScheduleManager.vue';
import ReportActionMenu from '@/components/ReportActionMenu.vue';
import ChartTypeSelector from '@/components/ChartTypeSelector.vue';
import Permission from "@/components/Permission.vue";
import { FEATURES, PREMIUM_MEASURE_CODES } from "@/config/featureConfig";
import ReportSort from '@/components/ReportSort.vue';
import { SortableColumn } from '@/types/sort';
import { InsightModel } from '@/models/insight';
import { isValidSyncDate } from '@/utils/isValidSyncDate';

interface Option {
  sortBy?: any;
  sortDesc?: any;
  page?: number;
  itemsPerPage?: number;
  multiSort?: boolean;
}

interface Range {
  start?: any;
  end?: any;
}

@Component({
  components: {
    TableReport,
    HeaderTitle,
    DateFilter,
    GroupByDimension,
    AnalyticsTypeFilter,
    ChatWithUs,
    BigNumberReport,
    FilterColumn,
    FeatureLockedNotify,
    ColumnSelector,
    ReportCard,
    ReportParam,
    ErrorMessage,
    ComparisonDate,
    Alert,
    ActionButton,
    LoadingSlider,
    SegmentSelector,
    EtlStatusPage,
    ReportSearch,
    OnboardCompleteConfirm,
    AppcueTrigger,
    CardColumnSelector,
    StartupOffer,
    DataSyncStatusBanner,
    ColorRuleEditor: () => import('@/components/ColorRuleEditor.vue'),
    ReportScheduleManager: () => import('@/components/schedule/ReportScheduleManager.vue'),
    ReportActionMenu,
    ChartTypeSelector,
    Permission,
    ReportSort,
    ReportAssistantDialog,
  },
})
export default class ReportPage extends Vue {

  // NEW PROPERTY ADDED HERE
  public displayMode: string = 'table';
  public tableViewMode: string = 'composition';
  
  // Report Assistant Dialog
  public showAssistantDialog: boolean = false;
  public assistantFilters: any[] = [];
  public assistantSort: any = null;
  public assistantColumns: any[] = [];

  // Computed property to bridge v-switch (boolean) and tableViewMode (string)
  get isCompositionViewSwitch(): boolean {
    return this.tableViewMode === 'composition';
  }
  set isCompositionViewSwitch(value: boolean) {
    this.tableViewMode = value ? 'composition' : 'standard';
  }

  public get reportPageContainerClass() {
    if (this.drawerOpen) {
      return 'report-page-container-drawer-open';
    }
    return '';
  }

  public get isMeasureSupported(): boolean {
    return this.canViewReport && 
           this.readyToGetReport && 
           !this.showLockInventoryOptimization &&
           InsightModel.isMeasureSupported(this.measure.code);
  }

  public get filterReportColumns() {
    let reportColumns: any = [];
    if (this.measure.filterDimensions.size() > 0) {
      reportColumns = this.measure.filterDimensions.toArray().map(dim => ({
        name: dim.label || dim.name,
        code: dim.fieldCode,
        dataType: dim.dataType,
        luisMapping: dim.code,
      }));
    }
    
    reportColumns = [...reportColumns, ...this.initReportFilterColumns];
    reportColumns = this.refineFilterColumns(reportColumns);
    
    return reportColumns;
  }

  public get getSelectedCardColumns() {

    const cardColumns = this.selectedCardColumns?.length > 0 
      ? this.selectedCardColumns 
      : this.initReportColumns;

    return cardColumns;
  }
  
  public get canSaveProductSegment() {
    const dimensions: any = ["by_product", "by_product_sku", "by_variant"];
    if (dimensions.includes(this.dimension.code)) {
      return true;
    }
    return false;
  }

  public get canCreateViewForTemplateReport() {
    if (
      this.customReport &&
      this.customReport.id &&
      this.customReport.id !== undefined
    ) {
      return true;
    }
    if (this.store.shopName !== "dariustest2.myshopify.com") {
      return false;
    }
    if (localStorage.getItem("canAddView") !== "true") {
      return false;
    }
    if (localStorage.getItem("loggedInFromHades") !== "true") {
      return false;
    }
    return true;
  }

  public get isAssistyAdmin() {
    if (this.store.shopName !== "dariustest2.myshopify.com") {
      return false;
    }
    if (localStorage.getItem("canAddView") !== "true") {
      return false;
    }
    if (localStorage.getItem("loggedInFromHades") !== "true") {
      return false;
    }
    return true;
  }

  public get daysToShowInventoryOptimization() {
    const now_timestamp: number = this.toTimestamp("");
    const installed_timestamp: number = this.toTimestamp(
      this.store.installedAt
    );
    const days = Math.round(
      (now_timestamp - installed_timestamp) / (24 * 60 * 60)
    );
    return this.lockInventoryOptimizationDays - days;
  }

  public get pageTitle() {
    let title: string = "";
    if (
      this.templateReport.id &&
      this.templateReport.dimension &&
      this.dimension.code === this.templateReport.dimension.code &&
      this.templateReport.analyticsType &&
      (this.analyticsTypeCode === this.templateReport.analyticsType.code ||
        (this.analyticsTypeCode === "" &&
          this.templateReport.analyticsType.code === null))
    ) {
      title = this.templateReport.name;
    } else {
      title = this.measure.name;
    }
    return title;
  }

  public get dateRangeText() {
    return `${dateFormat(this.dates[0], "mmm d, yyyy", true)} - ${dateFormat(
      this.dates[1],
      "mmm d, yyyy",
      true
    )}`;
  }
  @Prop({ default: null })
  public app!: any;
  @Prop({ default: () => new StoreModel() })
  public store!: StoreModel;
  public measure: MeasureModel = new MeasureModel();
  public dimension: DimensionModel = new DimensionModel();
  public initDimension: DimensionModel = new DimensionModel();
  public analyticsType: AnalyticsTypeModel = new AnalyticsTypeModel();
  public analyticsTypeCode: string = "";
  public newName: string = "";
  public readyToGetReport: boolean = false;
  public loading: boolean = false;
  public loadingSaveReport: boolean = false;
  public loadingSaveAs: boolean = false;
  public loadingRename: boolean = false;
  public loadingDelete: boolean = false;
  public dates: string[] = [];
  public defaultRange: string = "";
  public timeFilters = [
    "by_day",
    "by_week",
    "by_month",
    "by_quarter",
    "by_year",
  ];
  public customReport: CustomReportModel = new CustomReportModel();
  public newCustomReport: CustomReportModel = new CustomReportModel();
  public templateReport: TemplateReportModel = new TemplateReportModel();
  public filterDimensions: FilterDimensionList = new FilterDimensionList();
  public reportCardModel: ReportCardModel = new ReportCardModel();
  public useDefaultConfig: boolean = false;
  public dialogSaveAs: boolean = false;
  public saveAsOption: string = "new";
  public dialogRename: boolean = false;
  public dialogDelete: boolean = false;
  public dialogShareReport: boolean = false;
  public dialogSelectChartType: boolean = false;
  public linkCopied: boolean = false;
  public rule: RuleModel = new RuleModel({ code: "favorite_reports_count" });
  public rulePaidMeasure: RuleModel = new RuleModel({ code: "paid_measures" });
  public isInitFilterFromParam: boolean = false;
  public isInitReportParamFromParam: boolean = false;
  @Prop({ default: false })
  public enableAthena!: boolean;
  @Prop({ default: false })
  public isDataReady!: boolean;
  @Prop({ default: false })
  public isAthenaReady!: boolean;
  @Prop({ default: false })
  public isAthenaFinished!: boolean;
  @Prop({ default: "available" })
  public isLockedReports!: string;
  @Prop({ default: false })
  public hasSchemas!: boolean;
  @Prop({ default: () => new TemplateReportList() })
  public templateReports!: TemplateReportList;
  @Prop({ default: () => new MeasureList() })
  public measures!: MeasureList;
  public timeRangeLimit: string[] = []; // Initialize here
  public isLocked: string = "AVAILABLE";
  public reportFilters: FilterColumnList = new FilterColumnList();
  public filterColumns: FilterColumnList = new FilterColumnList();
  public initFilterColumns: FilterColumnList = new FilterColumnList();
  public reportParams: ParamList = new ParamList();
  public initReportParams: ParamList = new ParamList();
  public reportDefaultColumns: any = [];
  public reportColumns: any = [];
  public initReportColumns: any = [];
  public initReportFilterColumns: any = [];
  public selectedReportColumns: any = [];
  public savedReportColumns: any = [];
  public isResetFilterColumns: boolean = false;
  public customReports: CustomReportList = new CustomReportList();
  public pivotOption: string = "";
  public initPivotOption: string = "";
  public reportName: string = "";
  public columnFilterMenu: boolean = false;
  public groupByDimensionMenu: boolean = false;
  public filterDateRangeMenu: boolean = false;
  public reportGuideMenu: boolean = false;
  public columnSelectorMenu: boolean = false;
  public dateRangeMenu: boolean = false;
  public showActions: boolean = true;
  public breakpoint: string = "md";
  public pageLayoutShowChart: boolean = true; // RE-ENABLED to fix lint error
  public pageLayoutShowTable: boolean = true;
  public needRefreshTableData: boolean = false;
  public needRefreshMetricData: boolean = false;
  public dataError: MyErrorHandler = new MyErrorHandler("");
  public comparisonDateModel: ComparisonDateModel = new ComparisonDateModel();
  public initComparisonDate: ComparisonDateModel = new ComparisonDateModel();
  public isCreated: boolean = false;
  public showSetupMenu: boolean = false;
  public setupModule: string = "column";
  public chartType: string = "";
  public hasChart: boolean = true;
  public userguides: any[] = [];
  @Prop({ default: () => new DimensionColumnList() })
  public activeDimensions!: DimensionColumnList;
  public availableParams: ParamList = new ParamList();
  public showPivot: boolean = false;
  public resourceLockedCode: string = "paid_measures";
  public columnSelectorAction: string = "column";
  public initPageOptions: Option = {};
  public pageOptions: Option = {};
  public fullscreenDataTable: boolean = false;
  public calculatedMetrics: MetricList = new MetricList();
  public initCalculatedMetrics: MetricList = new MetricList();
  public isExpandPageMenu: boolean = true;
  public showSetting: boolean = true;
  public isFavoriteReport: boolean = false;
  public canAddFavorite: boolean = false;
  public productSegment: ProductSegmentModel = new ProductSegmentModel();
  public showLockInventoryOptimization: boolean = false;
  public lockInventoryOptimizationDays: number = 1;
  public dialogSaveCardToDashboard: boolean = false;
  public selectedDashboard: DashboardModel = new DashboardModel();
  public myDashboards: DashboardList = new DashboardList();
  public saveCardMessageColor: string = "";
  public saveCardMessage: string =
    "Please selelect a dashboard to save the card";
  public loadDashboardList: boolean = false;
  public saveReportToDashboardLoading: boolean = false;
  public createScheduleDialog: boolean = false;
  public selectedScheduleProfile: ScheduleProfileModel =
    new ScheduleProfileModel();
  public scheduleProfiles: ScheduleProfileList = new ScheduleProfileList();
  public isLoadingScheduleProfiles: boolean = false;
  public newScheduleTitle: string = "";
  public saveScheduleLoading: boolean = false;
  public schedule: ScheduleModel = new ScheduleModel();
  public createScheduleError: string = "";
  public searchData: FilterColumnModel = new FilterColumnModel();
  public dialogSaveOptions: boolean = false;
  public settingPages: any[] = [
    {
      measure: 'inventory_replenishment',
      title: 'Set Reorder Params',
      url: '/settings/reorder-parameters',
    },
    {
      measure: 'inventory_planning_date',
      title: 'Set Reorder Params',
      url: '/settings/reorder-parameters',
    },
    {
      measure: 'fixed_period_inventory_replenishment',
      title: 'Set Reorder Params',
      url: '/settings/reorder-parameters',
    },
    {
      measure: 'fixed_order_quantity_inventory_replenishment',
      title: 'Set Reorder Params',
      url: '/settings/reorder-parameters',
    },
    {
      measure: 'inventory_replenishment_min_max',
      title: 'Set Reorder Params',
      url: '/settings/reorder-parameters',
    },
    {
      measure: 'inventory_replenishment_min_max_stock_cover_days',
      title: 'Set Replenishment Params',
      url: '/settings/reorder-parameters',
    },
    {
      measure: 'inventory_transfer_suggestion',
      title: 'Config Transfer Rules',
      url: '/settings/internal-inventory-transfer-settings',
    },
  ];
  public datatableSize: string = "report-page-datatable-size-big";
  public showEditMetricColumns: boolean = false;
  public selectedCardColumns: any = [];
  public isDimensionChanged: boolean = false;
  private colorRules: ColorRuleList = new ColorRuleList();
  private showColorRuleEditorDialog: boolean = false;
  private expandedSections = {
    filters: true,
    params: true, 
    settings: false,
    actions: false,
    customize: false
  }
  public pinColumnCount: number = 3;
  private isScheduleEdit: boolean = false;
  private scheduleId: number = 0;
  private reportIndex: number = 0;
  private isTableReportLoading: boolean = true;
  private finalPermissionCheckComplete: boolean = false;
  private drawerOpen: boolean = false;
  private drawerTopPosition: number = 100; // Default top position
  private isSwitchingView: boolean = false;

  private getMetricColumns() {
    return this.reportColumns.filter((col: { dataType: string }) => !['text', 'date', 'datetime', 'image'].includes(col.dataType));
  }

  @Watch('drawerOpen')
  onDrawerOpenChanged(isOpen: boolean) {
    if (!this.readyToGetReport) {
      return;
    }
    if (isOpen) {
      EventBus.$emit('report-drawer-opened');
    } else {
      EventBus.$emit('report-drawer-closed');
    }
  }

  public async created() {
    // --- START localStorage read ---
    const savedMode = localStorage.getItem('reportTableViewMode');
    if (savedMode === 'standard') { // Check explicitly for 'composition'
      this.tableViewMode = 'standard';
    } else {
      this.tableViewMode = 'composition'; // Default to 'standard'
    }
    // --- END localStorage read ---

    this.finalPermissionCheckComplete = false;
    this.readyToGetReport = false;
    this.loading = true;
    this.checkIsMenuPinned();
    this.determineScreenSize(); // Initialize drawer state
    this.initializeDrawerState(); // Initialize drawer state
    for (const rule of this.store.currentPackage.rules.items) {
      if (rule.code === this.rule.code) {
        this.rule = rule;
      }
      if (rule.code === this.rulePaidMeasure.code) {
        this.rulePaidMeasure = rule;
      }
    }

    // Add resize handler but use arrow function to maintain 'this' context
    window.addEventListener('resize', () => this.determineScreenSize());

    await this.fullLoadData();

    // Emit initial state after drawer is potentially opened by initializeDrawerState/toggleDrawer
    if (this.drawerOpen) {
      EventBus.$emit('report-drawer-opened');
    } else {
      EventBus.$emit('report-drawer-closed');
    }

    if (this.isAthenaFinished) {
      try {
        const funnelEvent = new FunnelEventModel({
          name: 'VIEWED_FIRST_REPORT',
        });
        funnelEvent.create();
      } catch (error) {
        // skipped
      }
    }
    document.title = this.reportName;
    const searchModel: SearchModel = new SearchModel();
    searchModel.addRecentViewReport(this.templateReport.id);
    EventBus.$on("open-column-selector", async () => {
      this.showSetupMenu = true;
    });
    EventBus.$on("reload-report-data", async () => {
      await this.retryLoadingData();
    });
    EventBus.$on("update-pivot-option", async (pivotOption: any) => {
      this.pivotOption = pivotOption;
      this.initPivotOption = pivotOption;
      this.columnSelectorAction = "pivot";
      this.needRefreshTableData = true;
    });

    EventBus.$on("close-column-selector-form", async (data: any) => {
      this.showSetupMenu = false;
    });
    EventBus.$on("pin-menu", async (isPin: any) => {
      this.pinMenu(isPin);
    });

    EventBus.$on("apply-visulization-column", async (selectedColumns: any) => {
      this.showEditMetricColumns = false;
      this.needRefreshMetricData = true;
      this.selectedCardColumns = selectedColumns;
      this.replaceReportQuery();
    });

    this.loading = false;
    
  }


  /** 
   * Get report data
   */
   public getReportData() {
    let reportData: any = {};
    reportData.title = this.reportName;
    reportData.chartType = this.chartType;
    reportData.hasChart = this.hasChart;
    reportData.displayMode = this.displayMode;
    reportData.displayMode = this.displayMode; // ADDED
    reportData.selectedCardColumns = this.getSelectedCardColumns;
    reportData.measure = this.measure.code;
    reportData.dimension = this.dimension.code;
    reportData.analyticsType = this.analyticsType.code;
    reportData.defaultRange = this.defaultRange;
    reportData.dates = this.dates;
    reportData.filterColumns = this.filterColumns.items;
    reportData.pivotOption = this.pivotOption;
    reportData.reportParams = this.reportParams.items;
    reportData.reportColumns = this.reportColumns;
    if (this.savedReportColumns && this.savedReportColumns.length > 0) {
      reportData.savedReportColumns = this.savedReportColumns;
    } else {
      reportData.savedReportColumns = this.reportColumns;
    }
    reportData.pageOptions = this.pageOptions;
    reportData.productSegment = this.productSegment.id;
    reportData.calculatedMetrics = this.calculatedMetrics.items;
    reportData.comparisonDate = this.comparisonDateModel;
    if (this.colorRules && this.colorRules.items.length > 0) {
      reportData.colorRules = this.colorRules.items.filter(rule => rule.measure === this.measure.code);
    }
    reportData.pinColumnCount = this.pinColumnCount;
    return reportData;
  }

  private async loadDefaultColorRules() {
    // check if the color rules are already loaded
    if (this.colorRules.items.length > 0) {
      return;
    }
    try {
      const response = { data: (await import('@/services/color_rules_mock_data')).mockColorRules };
      this.colorRules = new ColorRuleList();
      const rules = response.data.rules.filter((rule: any) => rule.measure === this.measure.code);
      this.colorRules.mapData(rules);
    } catch (error) {
      // console.error('Failed to load default color rules:', error);
    }
  }

  public pinMenu(isPin: boolean = false) {
    if (isPin) {
      this.datatableSize = "report-page-datatable-size-small";
    } else {
      this.datatableSize = "report-page-datatable-size-big";
    }
  }

  public checkIsMenuPinned() {
    //get isPinMenu from local storage
    let isPinMenu = localStorage.getItem("isPinMenu") || "false";
    if (isPinMenu === "true") {
      if (this.fullscreenDataTable) {
        this.datatableSize = "report-page-datatable-size-small-fullscreen";
      } else {
        this.datatableSize = "report-page-datatable-size-small";
      }
    } else {
      if (this.fullscreenDataTable) {
        this.datatableSize = "report-page-datatable-size-big-fullscreen";
      } else {
        this.datatableSize = "report-page-datatable-size-big";
      }
    }
  }

  public get canShowSettingButton() {
    return this.settingPages.some((page) => page.measure === this.measure.code);
  }

  public get getSettingLabel() {
    return this.settingPages.find((page) => page.measure === this.measure.code)
      ?.title;
  }

  public goToSettingPage() {
    const page = this.settingPages.find(
      (page) => page.measure === this.measure.code
    );
    if (page) {
      this.$router.push(page.url);
    }
  }

  public async showCreateScheduleDialog() {
    this.isScheduleEdit = this.schedule && this.schedule.id ? true : false;
    this.scheduleId = this.schedule && this.schedule.id ? this.schedule.id : 0;
    this.reportIndex = Number(this.$route.params.report_index);
    this.createScheduleDialog = true;
  }

  public getInsightParameters() {
    return this.prepareReportOptions();
  }

  public showMessage(message: any) {
    if (message.type === 'success') {
      // Handle success message (e.g., show toast)
      console.log('Success:', message.text);
    } else if (message.type === 'error') {
      // Handle error message (e.g., show toast)
      console.error('Error:', message.text);
    }
  }

  public getScheduleData() {
    const scheduleData: any = {};
    scheduleData.name = this.reportName;
    scheduleData.measureCode = this.measure.code;
    scheduleData.measureName = this.measure.name;
    scheduleData.dimensionCode = this.dimension.code;
    scheduleData.dimensionName = this.dimension.name;
    scheduleData.analyticsTypeCode = "";
    scheduleData.pivotOption = "";
    scheduleData.timeRange = [];
    scheduleData.filterColumns = [];
    scheduleData.reportParams = [];
    scheduleData.reportColumns = [];
    scheduleData.calculatedMetrics = [];
    scheduleData.sortBy = this.generateSortByParam(
      this.pageOptions.sortBy,
      this.pageOptions.sortDesc
    );

    if (this.analyticsType && this.analyticsType.code) {
      scheduleData.analyticsTypeCode = this.analyticsType.code;
    }
    if (this.pivotOption) {
      scheduleData.pivotOption = this.pivotOption;
    }
    if (this.measure.hasTimerange) {
      if (this.defaultRange) {
        scheduleData.timeRange = [this.defaultRange];
      } else if (this.dates.length > 2) {
        scheduleData.timeRange = [this.dates[0], this.dates[1]];
      }
    }
    if (this.filterColumns && this.filterColumns.items.length > 0) {
      scheduleData.filterColumns = this.filterColumns.items;
    }
    if (this.savedReportColumns && this.savedReportColumns.length > 0) {
      scheduleData.reportColumns = this.savedReportColumns;
    }
    if (this.reportParams && this.reportParams.items.length > 0) {
      scheduleData.reportParams = this.reportParams.items;
    }
    if (this.calculatedMetrics && this.calculatedMetrics.items.length > 0) {
      scheduleData.calculatedMetrics = this.calculatedMetrics.items;
    }
    if (this.productSegment && this.productSegment.id) {
      scheduleData.productSegmentId = this.productSegment.id;
    }
    return scheduleData;
  }

  public generateSortByParam(sortBy: any = [], sortDesc: any = []) {
    if (!sortBy || sortBy.length === 0) {
      return [];
    }
    let index: number = 0;
    const sortByResult: any = [];
    for (let index = 0; index < sortBy.length; index++) {
      sortByResult.push({
        key: sortBy[index],
        order: sortDesc[index],
      });
    }

    return sortByResult;
  }

  public showCreateProductSegmentDialog() {
    EventBus.$emit("show-create-product-segment-dialog");
  }

  public async showSaveReportToDashboardDialog() {
    this.dialogSaveCardToDashboard = true;
    if (this.myDashboards.items.length === 0) {
      this.loadDashboardList = true;
      await this.myDashboards.fetch();
      this.loadDashboardList = false;
    }
  }

  public async saveReportToDashboard() {
    this.saveReportToDashboardLoading = true;
    this.saveCardMessageColor = "";
    this.saveCardMessage = "Please select a dashboard to save the card";
    const reportCard = new ReportCardModel();
    reportCard.name = this.reportName;
    // MODIFIED Condition
    if (this.hasChart && this.displayMode === 'visualization') { 
      reportCard.typeReport = this.chartType;
    } else {
      reportCard.typeReport = "TABLE";
    }
    reportCard.dimension = this.dimension;
    reportCard.measure = this.measure;
    reportCard.analyticsType = this.analyticsType;
    reportCard.filterColumns = this.filterColumns;
    reportCard.reportColumns = this.savedReportColumns;
    reportCard.reportParams = this.reportParams;
    reportCard.filterTimerange = this.dates;
    reportCard.dashboardIds = this.selectedDashboard.id;
    reportCard.productSegmentId = this.productSegment.id;
    reportCard.calculatedMetrics = this.calculatedMetrics;
    reportCard.pageOptions = this.pageOptions;
    if (this.defaultRange && this.defaultRange !== "custom") {
      reportCard.defaultRange = this.defaultRange;
    } else {
      reportCard.defaultRange = "30d";
    }
    reportCard.pivotOption = this.pivotOption;
    try {
      await this.selectedDashboard.fetch();
      await reportCard.create();
      this.selectedDashboard.reportCardList.add(reportCard);
      await this.selectedDashboard.updateCardOption();
      // this.selectedDashboard = new DashboardModel();
      const eventHandler = new EventHandler({
        store: this.store,
      });
      eventHandler.track("Create Report Card from report", {
        name: reportCard.name,
        id: reportCard.id,
        measure: reportCard.measure.code,
        dimension: reportCard.dimension ? reportCard.dimension.code : "",
      });
      EventBus.$emit("show-snackbar", {
        message: "Report has been saved to dashboard",
        color: "success",
      });
    } catch (e) {
      // ignore this
      this.saveReportToDashboardLoading = false;
      this.saveCardMessageColor = "red";
      this.saveCardMessage = "There is something wrong!";
      this.dialogSaveCardToDashboard = false;
      EventBus.$emit("show-snackbar", {
        message: "There is something wrong!",
        color: "error",
      });
    }
    this.saveReportToDashboardLoading = false;
    this.dialogSaveCardToDashboard = false;
  }

  public saveFavoriteReport() {
    this.isFavoriteReport = !this.isFavoriteReport;
    let data: any = {};
    if (this.customReport && this.customReport.id) {
      data = {
        name: this.customReport.name,
        id: this.customReport.id,
        type: "customReport",
      };
    } else if (this.templateReport && this.templateReport.id) {
      data = {
        name: this.templateReport.name,
        id: this.templateReport.id,
        type: "templateReport",
      };
    }
    if (this.isFavoriteReport) {
      this.sendMixpanelEvent("Save Favorite Report");
    } else {
      this.sendMixpanelEvent("Remove Favorite Report");
    }

    this.store.saveFavoriteReport(this.isFavoriteReport, data);
    EventBus.$emit("show-snackbar", {
      message: this.isFavoriteReport
        ? "Report has been added to favorite"
        : "Report has been removed from favorite",
      color: "success",
    });
  }

  public turnFullscreenTable(isFullscreenTable: boolean = false) {
    this.fullscreenDataTable = isFullscreenTable;
    if (isFullscreenTable) {
      this.drawerOpen = false; // Close the drawer when entering fullscreen mode
    }
    this.checkIsMenuPinned();
  }

  public actionPivotClick() {
    this.showSetupMenu = true;
    this.columnSelectorAction = "pivot";
    EventBus.$emit("close-column-view-form");
  }

  public actionShowAddCalculatedMetricForm() {
    this.showSetupMenu = true;
    this.columnSelectorAction = "metric";
  }

  public actionManageColumnClick() {
    this.showSetupMenu = true;
    this.columnSelectorAction = "column";
    EventBus.$emit("close-column-view-form");
  }

  public openAddMoreColumnDialogEvent() {
    EventBus.$emit("open-add-more-column-dialog-event");
  }

  public actionShowSetupColumn() {
    this.showSetupMenu = true;
    this.columnSelectorAction = "column";
    EventBus.$emit("close-column-view-form");
  }

  public actionCloseSetupMenu() {
    this.showSetupMenu = false;
    EventBus.$emit("close-column-view-form");
  }

  public expandPageMenu() {
    this.isExpandPageMenu = !this.isExpandPageMenu;
  }

  public updateCalculatedMetric(data: any) {
    if (!data || !data.metrics || data.metrics.items.length === 0) {
      return;
    }
    this.calculatedMetrics = data.metrics;
    this.showSetupMenu = false;
    this.saveReportOptions();
  }

  public isRightReport(
    templateReportId: number = 0,
    customReportId: number = 0
  ) {
    if (
      this.customReport &&
      this.customReport.id &&
      this.customReport.id !== undefined
    ) {
      if (this.customReport.id === customReportId) {
        return true;
      }
    } else {
      if (this.templateReport.id === templateReportId) {
        return true;
      }
    }
    return false;
  }
  
  public saveReportOptions() {
    if (
      this.customReport &&
      this.customReport.id &&
      this.customReport.id !== undefined
    ) {
      this.customReport.calculatedMetrics = this.calculatedMetrics;
      this.customReport.updateOptions();
    } else {
      if (this.canCreateViewForTemplateReport) {
        this.updateOptions();
      }
    }
  }

  public updateOptions() {
    if (!this.templateReport.id) {
      return;
    }
    this.templateReport.pageOptions = this.pageOptions;
    this.templateReport.calculatedMetrics = this.calculatedMetrics;
    this.templateReport.updateOptions();
  }

  public updateReportParams() {
    if (!this.templateReport.id) {
      return;
    }
    this.templateReport.pageOptions = this.pageOptions;
    this.templateReport.reportParams = this.reportParams;
    this.templateReport.calculatedMetrics = this.calculatedMetrics;
    this.templateReport.updateReportParams();
  }

  public async initActiveDimensions() {
    this.activeDimensions = new DimensionColumnList();
    await this.activeDimensions.fetch();
  }

  public async initAvailableParams() {
    if (!this.activeDimensions || this.activeDimensions.size() === 0) {
      await this.initActiveDimensions();
    }
    this.availableParams = new ParamList();
    if (this.measure.params && this.measure.params.size() > 0) {
      for (const item of this.measure.params.items) {
        this.availableParams.add(item);
      }
    }
    for (const dimension of this.activeDimensions.items) {
      if (dimension.luis_mapping === this.dimension.code) {
        if (dimension.params && dimension.params.size() > 0) {
          this.dimension.params = dimension.params;
          for (const item of dimension.params.items) {
            if (this.canAddAvailableParam(item)) {
              this.availableParams.add(item);
            }
          }
        }
      }
    }
  }

  public initParamFromDefaultValue() {
    if (!this.initReportParams || this.initReportParams.items.length === 0) {
      for (const item of this.availableParams.items) {
        if (item.value && item.value.length > 0) {
          const param = new ParamModel();
          param.NeedApiRequest = item.NeedApiRequest;
          param.code = item.code;
          param.listValues = item.listValues;
          param.name = item.name;
          param.sortOrder = item.sortOrder;
          param.value = item.value;
          param.valueType = item.valueType;
          param.dataType = item.dataType;
          param.default = item.default;
          if (this.canAddInitReportParams(param)) {
            this.initReportParams.add(param);
          }
        }
      }
      if (this.initReportParams.size() > 0) {
        this.reportParams = this.initReportParams;
      }
    }
  }

  public canAddInitReportParams(param: ParamModel) {
    for (const item of this.initReportParams.items) {
      if (item.code === param.code) {
        return false;
      }
    }
    return true;
  }

  public canAddAvailableParam(param: ParamModel) {
    for (const item of this.availableParams.items) {
      if (item.code === param.code) {
        return false;
      }
    }
    return true;
  }

  public openChat(openChatMessage: string) {
    // @ts-ignore: Unreachable code error
    this.$crisp.do("chat:open");
    // @ts-ignore: Unreachable code error
    // this.$crisp.do('message:send', ['text', this.$t(`chat_with_us.${this.defaultMessage}`)]);
    this.$crisp.do("message:send", ["text", openChatMessage]);
  }

  public changeChartType(chartType: string) {
    this.displayMode = 'visualization'; // Ensure viz mode is active
    this.dialogSelectChartType = false;
    this.chartType = chartType;
    // this.turnonChart(); // REMOVED call to redundant method
    EventBus.$emit("report-card-change-chart-type", {type: this.chartType, id: this.reportCardModel.id});
    //this.needRefreshMetricData = true; 
  }

  public showExportDialog() {
    EventBus.$emit("show-download-dialog", { reportName: this.reportName || "" });
  }

  public async showSaveReportDialog() {
    await this.refreshCustomReportsCount();
    this.dialogSaveAs = true;
  }

  public showGeneratePODialog() {
    EventBus.$emit("show-generate-po-dialog");
  }

  public get canGeneratePO() {
    const repelnishemntMeasures: any = [
      'inventory_replenishment',
      'inventory_planning_date',
      'fixed_period_inventory_replenishment',
      'fixed_order_quantity_inventory_replenishment',
      'inventory_replenishment_min_max',
      'inventory_replenishment_min_max_stock_cover_days',
      'inventory_replenishment_bundles',
    ];
    //check if the current measure code is in the list
    return repelnishemntMeasures.includes(this.measure.code);
  }


  public getMenuItemStyle(hover: boolean = true) {
    let style: string = "";
    if (hover) {
      style = style + "background-color: #d4d4d4;";
    }
    if (this.isExpandPageMenu) {
      style = style + "padding: 0 16px;";
    }
    return style;
  }

  public error(error: any) {
    if (error instanceof MyErrorHandler) {
      this.dataError = error;
    } else {
      this.dataError = new MyErrorHandler("Unexpected Exception");
    }
  }

  public async retryLoadingData() {
    this.dataError = new MyErrorHandler("");
    this.needRefreshTableData = true;
    this.needRefreshMetricData = true;
    this.sendMixpanelEvent("retry loading data");
  }

  public refreshTableDataCompleted() {
    this.needRefreshTableData = false;
  }

  public refreshMetricDataCompleted() {
    this.needRefreshMetricData = false;
  }

  public needRefreshTableDataEvent() {
    this.needRefreshTableData = true;
  }
  public needRefreshMetricDataEvent() {
    this.needRefreshMetricData = true;
  }

  public changePageLayout(
    showChart: boolean = true,
    showTable: boolean = true
  ) {
    this.pageLayoutShowChart = showChart;
    this.pageLayoutShowTable = showTable;
  }
  public determineScreenSize() {
    this.breakpoint = this.$vuetify.breakpoint.name;
    if (["xs", "sm"].includes(this.breakpoint)) {
      this.showActions = false;
      this.drawerTopPosition = 15; // Smaller top position for mobile
    } else {
      this.drawerTopPosition = 15; // Default top position for larger screens
    }
  }
  
  public initializeDrawerState() {
    this.drawerOpen = false; // It starts false
    this.toggleDrawer(); // This flips it to true
    // EventBus emit for initial state will now be handled in created() after fullLoadData and drawer state is set.
  }

  public initReportCardModel() {
    const itemData = {
      id: 10,
      sortOrder: 10,
      typeReport: this.chartType,
      dimensionCode: this.dimension.code,
      measureCode: this.measure.code,
      analyticsTypeCode: this.analyticsType.code,
      name: this.reportName,
      defaultRange: this.defaultRange,
      isDetail: false,
      cardHeightSize: 1,
      cardWidthSize: 12,
      selectedReportColumns: this.savedReportColumns,
    };
    //check if the typeReport is LINE_CHART or BAR_CHART
    //then set isDetail to true
    if (["LINE_CHART", "BAR_CHART"].includes(itemData.typeReport)) {
      itemData.isDetail = true;
    }

    this.reportCardModel = new ReportCardModel();
    this.reportCardModel.mapData(itemData);
  }

  public showDialogSelectChartType() {
    this.dialogSelectChartType = true;
  }

  public showRenameDialog() {
    this.dialogRename = true;
  }

  public showDeleteDialog() {
    this.dialogDelete = true;
  }

  public showSaveAsDialog() {
    this.dialogSaveAs = true;
  }

  public showShareDialog() {
    this.dialogShareReport = true;
  }
  public printReport() {
    window.print();
    this.sendMixpanelEvent("print report");
  }

  public refineFilterColumns(reportColumns: any) {
    const duplicateColumns: any = {
      "Item[Item Category]": "product_collections",
      product_tag: "product_tags",
      order_tag: "order_tags",
      customer_tag: "customer_tags",
    };

    for (const column of reportColumns) {
      if (Object.keys(duplicateColumns).includes(column.code)) {
        let index: number = 0;
        for (const column2 of reportColumns) {
          if (column2.code === duplicateColumns[column.code]) {
            reportColumns.splice(index, 1);
            break;
          }
          index++;
        }
      }
    }
    return reportColumns;
  }

  public getShortReportName(reportName: string) {
    const maxLength: number = 55;
    if (!reportName || reportName === undefined) {
      return "";
    }
    if (reportName.length > maxLength) {
      return reportName.substring(0, maxLength) + "...";
    }
    //make report name to lowercase
    reportName = reportName.toLowerCase();
    //make the first character of the report name to uppercase
    reportName = reportName.charAt(0).toUpperCase() + reportName.slice(1);
    if (this.schedule && this.schedule.id) {
      return 'updating schedule:#'+ this.schedule.id + ' - ' + reportName;
    }
    return reportName;
  }

  public getDate(date: string) {
    return dateFormat(date, "mmm d, yyyy");
  }

  public async CopyReportLink() {
    this.sendMixpanelEvent("Copy link");
    const shopify_store = this.store.shopName || "";
    const storeName = shopify_store.replace(".myshopify.com", "");
    let link = `https://admin.shopify.com/store/${storeName}/apps/assisty${this.$route.fullPath}`;
    navigator.clipboard.writeText(link);
    this.linkCopied = true;
    EventBus.$emit("show-snackbar", "Link copied to clipboard");
    await this.sleep(3000);
    this.linkCopied = false;
  }



  public operatorName(value: string) {
    const operators: any = [
      { name: "equal", value: "eq" },
      { name: "equal (case-insensitive)", value: "eqi" },
      { name: "not equal", value: "ne" },
      { name: "is lower than", value: "lt" },
      { name: "is lower than or equal to", value: "lte" },
      { name: "is greater than", value: "gt" },
      { name: "is greater than or equal to", value: "gte" },
      { name: "contains", value: "contains" },
      { name: "not contains", value: "notContains" },
      { name: "is blank", value: "null" },
      { name: "is not blank", value: "notNull" },
    ];
    for (const item of operators) {
      if (item.value === value) {
        return item.name;
      }
    }
    return value;
  }

  public filterValuesToString(values: any) {
    if (!values) {
      return "";
    }
    // Handle case where values is not an array
    if (!Array.isArray(values)) {
      // If it's a single value, just return it as a string
      return String(values);
    }
    return values.join(" or ");
  }

  public goBack() {
    let goBackUrl: string = "/";
    if (this.$route.params.custom_report_id) {
      goBackUrl = "/my-reports";
    }

    if (this.$route.params.type_id) {
      if (this.store.currentPackage.name !== "Free") {
        goBackUrl =
          "/reports/" +
          this.$route.params.type_id +
          "/" +
          this.$route.params.type_name;
      } else {
        if (this.templateReport.isLocked === "ISLOCKED") {
          goBackUrl =
            "/premium_reports/true/" +
            this.$route.params.type_id +
            "/" +
            this.$route.params.type_name;
        } else {
          goBackUrl =
            "/reports/" +
            this.$route.params.type_id +
            "/" +
            this.$route.params.type_name;
        }
      }
    }

    if (goBackUrl !== "/") {
      this.$router.push(goBackUrl);
    } else {
      this.$router.go(-1);
    }
  }

  public async fullLoadData() {
    this.finalPermissionCheckComplete = false;
    this.loading = true;
    this.readyToGetReport = false;

    try {
      await this.loadReportConfiguration();
      await this.loadDefaultColorRules();

      // Load custom reports count for save dialog limit check (non-blocking)
      await this.refreshCustomReportsCount();

      // Mark that the essential data for permission check is loaded
      this.finalPermissionCheckComplete = true; 

      await this.initializeReportSettings();
    } finally {
      this.loading = false;
      this.readyToGetReport = true;
    }
  }

  private async loadReportConfiguration() {
    const reportQuery = this.$route.query.report_query || '';
    await this.handleStandardLoad();
    if (reportQuery) {
      this.assignReportQuery(reportQuery as string);
      this.getReportNameFromUrl();
    }
  }

  private getReportNameFromUrl() {
    const reportName = this.$route.params.report_name;
    if (reportName) {
      //remove the - from the report name
      this.reportName = reportName.replace(/-/g, ' ');
      //make the first letter of the report name to uppercase
      this.reportName = this.reportName.charAt(0).toUpperCase() + this.reportName.slice(1);
    }
  }

  private async initializeReportSettings() {
    this.autoDetectTimerangeLimit();
    this.autoDetectDimension();
    await this.setDefaultTimerange();
    this.getDatesFromDefaultTimeRange(this.defaultRange);
    this.rebuildSaveAsName();
    this.initParamFromDefaultValue();
    this.initReportCardModel();
    this.sendMixpanelEvent();
  }

  private async handleStandardLoad() {
    await this.defineMeasure();
    if (this.$route.params.custom_report_id) {
      await this.loadReportFromCustomReport();
      // Flow C: Review ask when viewing a saved custom report (recurring weekly)
      postUnlockReviewService.checkAndTriggerReview(this.store.id, 'favorite_reports_count');
    } else if (
      this.$route.params.measure_code &&
      this.$route.params.measure_code !== undefined
    ) {
      await this.loadReportFromUrlParams();
    } else if (
      this.$route.params.template_report_id &&
      this.$route.params.template_report_id !== undefined
    ) {
      await this.loadReportFromTemplateReport();
    } else if (
      this.$route.params.schedule_id &&
      this.$route.params.schedule_id !== undefined
    ) {
      await this.loadReportFromScheduleData();
    } else if (
      this.$route.params.card_id &&
      this.$route.params.card_id !== undefined
    ) {
      await this.loadReportFromReportCard();
    } else if (
      this.$route.params.report_name &&
      this.$route.params.report_name !== undefined
    ) {
      this.reportName = this.$route.params.report_name;
    } else {
      this.error(new MyErrorHandler("Invalid report"));
    }
  }

  public autoDetectDimension() {
    if (this.measure.dimensions.size() > 0) {
      if (!this.dimension.code) {
        if (this.measure.hasTimerange) {
          for (const dimension of this.measure.dimensions.items) {
            if (
              [
                "by_day",
                "by_week",
                "by_month",
                "by_quarter",
                "by_year",
              ].includes(dimension.code)
            ) {
              this.dimension = dimension;
              break;
            }
          }
        } else {
          this.dimension = this.measure.dimensions.items[0];
        }
      }
    } else {
      this.measure.hasTimerange = false;
    }
  }

  public async loadReportFromUrlParams() {
    // get report from params
    if (
      this.$route.params.measure_code &&
      this.$route.params.measure_code !== undefined
    ) {
      if (this.$route.query.title) {
        this.reportName = this.$route.query.title + "";
      } else {
        this.reportName = this.measure.name;
      }
      if (!this.canShowInventoryOptimization(this.measure.code)) {
        this.showLockInventoryOptimization = true;
        return;
      }
      this.loadDimensionFromParam();
      this.loadDateRangeFromParam();
      this.loadAnalyticTypeFronmParam();
      this.loadFilterColumnFromParam();
      await this.initAvailableParams();
      this.loadReportParamsFromUrl();
      // get pivotOption
      this.pivotOption = this.$route.query.pivot_option + "";
      this.initPivotOption = this.pivotOption;
    }
  }

  public loadAnalyticTypeFronmParam() {
    // get analytic type
    const analyticType = this.$route.query.analytic;
    if (analyticType && analyticType !== undefined) {
      this.analyticsType = new AnalyticsTypeModel({
        id: "0",
        name: analyticType,
        code: analyticType,
      });
    }
  }
  public loadDimensionFromParam() {
    // get dimension
    const dimensionCode = this.$route.query.dimension;
    for (const dimension of this.measure.dimensions.items) {
      if (dimension.code === dimensionCode) {
        this.dimension = dimension;
        break;
      }
    }
  }
  public loadDateRangeFromParam() {
    // get timeRange Filter
    const defaultRange = this.$route.query.defaultRange;
    if (defaultRange && defaultRange !== undefined) {
      this.defaultRange = defaultRange + "";
    }
    const start = this.$route.query.start_time;
    const end = this.$route.query.end_time;
    if (start && start !== undefined) {
      this.dates.push(start + "");
      if (end && end !== undefined) {
        this.dates.push(end + "");
      }
      this.dates.push("custom");
    }
  }
  public loadFilterColumnFromParam() {
    // get filter
    const filterParam = this.$route.query.filter + "";
    const filters = filterParam.split("_col_");
    if (filters.length > 0) {
      this.filterColumns = new FilterColumnList();
      for (const filter of filters) {
        const filterCond = filter.split("__");
        if (filterCond.length < 4) {
          continue;
        }
        const itemData = {
          name: filterCond[0],
          code: filterCond[1],
          operator: filterCond[2],
          value: filterCond[3].split("_or_"),
          dataType: filterCond[4],
        };
        const filterColumn = new FilterColumnModel(itemData);
        this.filterColumns.add(filterColumn);
      }
      this.initFilterColumns = this.filterColumns;
      this.reportFilters = this.initFilterColumns;
      this.isInitFilterFromParam = true;
    }
  }

  public loadReportParamsFromUrl() {
    const paramData = this.$route.query.params + "";
    const params = paramData.split("_param_");
    if (params.length > 0) {
      const reportParams: ParamList = new ParamList();
      for (const param of params) {
        const paramAttributes = param.split("__");
        if (paramAttributes.length < 5) {
          continue;
        }
        const paramModel = new ParamModel({
          name: paramAttributes[0],
          code: paramAttributes[1],
          dataType: paramAttributes[2],
          valueType: paramAttributes[3],
          value: paramAttributes[4].split("_or_"),
        });
        reportParams.add(paramModel);
      }
      this.loadReportParamFromSavedParams(reportParams);
      this.initReportParams = this.reportParams;
      this.isInitReportParamFromParam = true;
    }
  }

  public async loadReportFromCustomReport() {
    // load data from custom report
    if (this.$route.params.custom_report_id) {
      this.reportName = this.customReport.name;

      // First check if custom report has reportQuery
      if (this.customReport.reportQuery) {
        try {
          // Set custom report ID before assigning report query
          if (this.$route.params.custom_report_id) {
            this.customReport.id = Number(this.$route.params.custom_report_id);
          }

          // Use existing assignReportQuery to handle report query parameters
          this.$route.query.report_query = this.customReport.reportQuery;
          await this.assignReportQuery(this.customReport.reportQuery);
        } catch (e) {
          // If there's an error parsing report query, fall back to regular custom report loading
          // console.warn('Error parsing report query from custom report:', e);
        }
      }

      // Fall back to regular custom report loading if no valid report query
      this.reportName = this.customReport.name;
      this.loadDimensionFromCustomReport();
      this.loadAnalyticTypeFromCustomReport();
      await this.initAvailableParams();
      this.filterDimensions = this.customReport.filterDimensions;
      this.initFilterColumns = this.customReport.filterColumns;
      this.reportFilters = this.initFilterColumns;
      this.loadReportParamFromSavedParams(this.customReport.reportParams);
      this.initReportParams = this.customReport.reportParams;
      this.filterColumns = this.customReport.filterColumns;
      this.pivotOption = this.customReport.pivotOption;
      this.initPivotOption = this.pivotOption;
      this.savedReportColumns = this.customReport.reportColumns;
      this.selectedReportColumns = this.customReport.reportColumns;
      this.pageOptions = this.customReport.pageOptions;
      this.initPageOptions = this.customReport.pageOptions;
      this.initCalculatedMetrics = this.customReport.calculatedMetrics;
      this.calculatedMetrics = this.customReport.calculatedMetrics;

      if (this.customReport.productSegmentId) {
        this.productSegment.id = this.customReport.productSegmentId;
      }
      if (this.customReport.defaultRange) {
        this.defaultRange = this.customReport.defaultRange;
      }
      if (this.customReport.filterTimerange) {
        this.dates = this.customReport.filterTimerange;
      }
    }
  }

  public get getReportName() {
    //I want to make the report name with the first letter in uppercase
    //and make all the other letters in lowercase
    if (!this.reportName || this.reportName === undefined) {
      return "";
    }
    this.reportName = this.reportName.toLowerCase();
    return this.reportName.charAt(0).toUpperCase() + this.reportName.slice(1);
  }

  public async loadReportFromScheduleData() {
    if (
      this.$route.params.schedule_id &&
      this.$route.params.schedule_id !== undefined
    ) {
      this.schedule.id = Number(this.$route.params.schedule_id);
      const reportIndex = Number(this.$route.params.report_index);
      const reportData: any = this.schedule.options[reportIndex];
      this.reportName = this.schedule.title;
      for (const dimension of this.measure.dimensions.items) {
        if (dimension.code === reportData.dimensionCode) {
          this.dimension = dimension;
        }
      }
      let analyticsTypeCode = "top_high";
      if (reportData.analyticsType) {
        analyticsTypeCode = reportData.analyticsTypeCode;
      }
      for (const analyticsType of this.measure.analyticsTypes.items) {
        if (analyticsType.code === analyticsTypeCode) {
          this.analyticsType = analyticsType;
          this.analyticsTypeCode = analyticsType.code;
        }
      }

      await this.initAvailableParams();
      if (reportData.reportParams && reportData.reportParams.length > 0) {
        const scheduleParams: ParamList = new ParamList();
        scheduleParams.mapData(reportData.reportParams);
        this.loadReportParamFromSavedParams(scheduleParams);
        this.initReportParams = this.reportParams;
      }

      if (reportData.filterColumns && reportData.filterColumns.length > 0) {
        const scheduleFilterColumns: FilterColumnList = new FilterColumnList();
        scheduleFilterColumns.mapData(reportData.filterColumns);
        this.filterColumns = scheduleFilterColumns;
        this.initFilterColumns = scheduleFilterColumns;
      } else {
        this.filterColumns = new FilterColumnList();
        this.initFilterColumns = new FilterColumnList();
      }
      this.reportFilters = this.initFilterColumns;
      if (reportData.pivotOption) {
        this.pivotOption = reportData.pivotOption;
        this.initPivotOption = this.pivotOption;
      }

      if (reportData.reportColumns && reportData.reportColumns.length > 0) {
        this.savedReportColumns = reportData.reportColumns;
        this.selectedReportColumns = reportData.reportColumns;
      }

      if (reportData.pageOptions) {
        this.pageOptions = reportData.pageOptions;
        this.initPageOptions = reportData.pageOptions;
      }
      if (
        reportData.calculatedMetrics &&
        reportData.calculatedMetrics.length > 0
      ) {
        this.initCalculatedMetrics = reportData.calculatedMetrics;
        this.calculatedMetrics = reportData.calculatedMetrics;
      }

      if (reportData.productSegmentId) {
        this.productSegment.id = reportData.productSegmentId;
      }
      if (reportData.timeRange && reportData.timeRange.length === 1) {
        this.defaultRange = reportData.timeRange[0];
      } else if (reportData.timeRange && reportData.timeRange.length >= 2) {
        this.defaultRange = reportData.timeRange[2];
        this.dates = reportData.timeRange;
      }
    }
  }

  public async loadReportFromReportCard() {
    if (
      this.$route.params.card_id &&
      this.$route.params.card_id !== undefined
    ) {
      this.reportName = this.reportCardModel.name;
      for (const dimension of this.measure.dimensions.items) {
        if (
          this.reportCardModel.dimension &&
          dimension.code === this.reportCardModel.dimension.code
        ) {
          this.dimension = dimension;
        }
      }
      let analyticsTypeCode = "top_high";
      if (this.reportCardModel.analyticsType && this.reportCardModel.analyticsType.code) {
        analyticsTypeCode = this.reportCardModel.analyticsType.code;
      }
      for (const analyticsType of this.measure.analyticsTypes.items) {
        if (analyticsType.code === analyticsTypeCode) {
          this.analyticsType = analyticsType;
          this.analyticsTypeCode = analyticsType.code;
        }
      }

      await this.initAvailableParams();
      if (this.reportCardModel.reportParams && this.reportCardModel.reportParams.items.length > 0) {
        const params: ParamList = new ParamList();
        this.loadReportParamFromSavedParams(this.reportCardModel.reportParams);
        this.initReportParams = this.reportParams;
      }

      if (
        this.reportCardModel.filterColumns &&
        this.reportCardModel.filterColumns.items.length > 0
      ) {
        this.filterColumns = this.reportCardModel.filterColumns;
        this.initFilterColumns = this.reportCardModel.filterColumns;
      } else {
        this.filterColumns = new FilterColumnList();
        this.initFilterColumns = new FilterColumnList();
      }
      this.reportFilters = this.initFilterColumns;
      if (this.reportCardModel.pivotOption) {
        this.pivotOption = this.reportCardModel.pivotOption;
        this.initPivotOption = this.pivotOption;
      }
      if (this.reportCardModel.typeReport === 'TABLE') {
        this.savedReportColumns = this.reportCardModel.reportColumns;
        this.selectedReportColumns = this.reportCardModel.reportColumns;
      } else {
        this.savedReportColumns = [];
        this.selectedReportColumns = [];
      }

      if (this.reportCardModel.pageOptions) {
        this.pageOptions = this.reportCardModel.pageOptions;
        this.initPageOptions = this.reportCardModel.pageOptions;
      }
      if (
        this.reportCardModel.calculatedMetrics &&
        this.reportCardModel.calculatedMetrics.items.length > 0
      ) {
        this.initCalculatedMetrics = this.reportCardModel.calculatedMetrics;
        this.calculatedMetrics = this.reportCardModel.calculatedMetrics;
      }

      if (this.reportCardModel.productSegmentId) {
        this.productSegment.id = this.reportCardModel.productSegmentId;
      }
      if (
        this.reportCardModel.filterTimerange &&
        this.reportCardModel.filterTimerange.length === 1
      ) {
        this.defaultRange = this.reportCardModel.filterTimerange[0];
      } else if (
        this.reportCardModel.filterTimerange &&
        this.reportCardModel.filterTimerange.length >= 2
      ) {
        this.defaultRange = this.reportCardModel.filterTimerange[2];
        this.dates = this.reportCardModel.filterTimerange;
      }
    }
  }

  public loadReportParamFromSavedParams(params: ParamList) {
    const newParams: ParamList = new ParamList();
    for (const param of params.items) {
      for (const item of this.availableParams.items) {
        if (param.code === item.code) {
          param.valueType = item.valueType;
          param.listValues = item.listValues;
          param.dataType = item.dataType;
          newParams.add(param);
        }
      }
    }
    this.reportParams = newParams;
  }

  public loadAnalyticTypeFromCustomReport() {
    if (
      this.customReport.analyticsType &&
      this.customReport.analyticsType.code
    ) {
      for (const analyticsType of this.measure.analyticsTypes.items) {
        if (analyticsType.code === this.customReport.analyticsType.code) {
          this.analyticsType = analyticsType;
          this.analyticsTypeCode = analyticsType.code;
        }
      }
    }
  }

  public loadDimensionFromCustomReport() {
    if (this.customReport.dimension) {
      for (const dimension of this.measure.dimensions.items) {
        if (dimension.code === this.customReport.dimension.code) {
          this.dimension = dimension;
        }
      }
    }
  }

  public canShowInventoryOptimization(measureCode: string) {
    const restrictMeasureList: any = [
      "sell_through_rate",
      "inventory_to_sales_ratio",
      "stock_availability",
      "inventory_turrnover",
      "aging_stock",
      "aging_stock_summary",
      "lost_revenue",
      "inventory_movement",
      "historical_inventory",
    ];
    if (!restrictMeasureList.includes(measureCode)) {
      return true;
    }
    const now_timestamp: number = this.toTimestamp("");
    const installed_timestamp: number = this.toTimestamp(
      this.store.installedAt
    );
    if (this.daysToShowInventoryOptimization <= 0) {
      return true;
    }
    return false;
  }

  public toTimestamp(strDate: string = "") {
    let datum: number = 0;
    if (!strDate) {
      datum = Date.now();
    } else {
      datum = Date.parse(strDate);
    }
    return datum / 1000;
  }

  public async loadReportFromTemplateReport() {
    // load data from report template
    if (this.$route.params.template_report_id) {
      this.reportName = this.templateReport.name;
      this.isLocked = this.templateReport.isLocked || "available";
      if (!this.canShowInventoryOptimization(this.measure.code)) {
        this.showLockInventoryOptimization = true;
        return;
      }
      this.loadDimensionFromTemplateReport();
      this.loadAnalyticTypeFromTemplateReport();
      await this.initAvailableParams();
      if (this.templateReport.filterTimerange) {
        this.dates = this.templateReport.filterTimerange;
      }
      this.initFilterColumns = this.templateReport.filterColumns;
      this.filterColumns = this.templateReport.filterColumns;
      this.reportFilters = this.initFilterColumns;
      if (this.templateReport.pivotOption) {
        this.pivotOption = this.templateReport.pivotOption;
        this.initPivotOption = this.pivotOption;
      }
      if (this.templateReport.reportParams) {
        this.loadReportParamFromSavedParams(this.templateReport.reportParams);
      }
      this.initReportParams = this.templateReport.reportParams;
      this.pageOptions = this.templateReport.pageOptions;
      this.initPageOptions = this.templateReport.pageOptions;
      this.initCalculatedMetrics = this.templateReport.calculatedMetrics;
      this.calculatedMetrics = this.templateReport.calculatedMetrics;
    }
  }

  public loadAnalyticTypeFromTemplateReport() {
    if (
      this.templateReport.analyticsType &&
      this.templateReport.analyticsType.code
    ) {
      for (const analyticsType of this.measure.analyticsTypes.items) {
        if (analyticsType.code === this.templateReport.analyticsType.code) {
          this.analyticsType = analyticsType;
          this.analyticsTypeCode = analyticsType.code;
        }
      }
    }
  }

  public loadDimensionFromTemplateReport() {
    if (this.templateReport.dimension) {
      for (const dimension of this.measure.dimensions.items) {
        if (dimension.code === this.templateReport.dimension.code) {
          this.dimension = dimension;
        }
      }
    }
  }

  public sendMixpanelEvent(name: string = "") {
    const eventName = name ? name : "View report";
    let reportName = this.reportName;
    let isCustomReport: boolean = false;
    let isTempalteReport: boolean = false;
    if (!reportName) {
      reportName = this.measure.name + " - " + this.dimension.name;
    }
    if (this.$route.params.custom_report_id) {
      isCustomReport = true;
    }
    if (this.$route.params.template_report_id) {
      isTempalteReport = true;
    }
    try {
      const eventHandler = new EventHandler({
        store: this.store,
      });
      eventHandler.track(eventName, {
        report_name: reportName,
        dimension: this.dimension.code,
        measure: this.measure.code,
        analytic: this.analyticsType.code,
        is_template_teport: isTempalteReport,
        is_custom_report: isCustomReport,
      });
    } catch (e) {
      // ignore this catch
    }
  }

  public async defineTemplateReport() {
    await this.templateReport.fetch();
    return this.templateReport;
  }

  public async defineMeasure() {
    this.measure = new MeasureModel();
    if (this.$route.params.custom_report_id) {
      this.customReport.id = Number(this.$route.params.custom_report_id);
      await this.customReport.fetch();
      this.measure.code = this.customReport.measure.code;
    } else if (
      this.$route.params.measure_code &&
      this.$route.params.measure_code !== undefined
    ) {
      // exception for inventory quantity
      let measureCode = this.$route.params.measure_code;
      if (measureCode === 'inventory-quantity') {
        measureCode = 'Actual Inventory Quantity';
      } else if (measureCode === 'inventory-value') {
        measureCode = 'Actual Inventory Cost';
      } else {
        // replace - with _
        measureCode = measureCode.replace(/-/g, '_');
      }
      console.log('measureCode', measureCode);
      this.measure.code = measureCode;
    } else if (
      this.$route.params.template_report_id &&
      this.$route.params.template_report_id !== undefined
    ) {
      this.templateReport.id = Number(this.$route.params.template_report_id);
      this.templateReport = await this.defineTemplateReport();
      this.measure.code = this.templateReport.measure.code;
    } else if (
      this.$route.params.schedule_id &&
      this.$route.params.schedule_id !== undefined
    ) {
      this.reportIndex = Number(this.$route.params.report_index);
      this.scheduleId = Number(this.$route.params.schedule_id);
      this.schedule = new ScheduleModel();
      this.schedule.id = this.scheduleId;
      await this.schedule.fetch();
      const reportData: any = this.schedule.options[this.reportIndex];
      
      this.measure.code = reportData.measureCode;
    } else if (
      this.$route.params.card_id &&
      this.$route.params.card_id !== undefined
    ) {
      this.reportCardModel = new ReportCardModel();
      this.reportCardModel.id = Number(this.$route.params.card_id);
      await this.reportCardModel.fetch();

      this.measure = this.reportCardModel.measure;
    } else {
      this.error(new MyErrorHandler("Invalid report"));
    }


    for (const measure of this.measures.items) {
      if (measure.code === this.measure.code) {
        this.measure = measure;
        return;
      }
    }
    await this.measure.fetch();
  }

  public async setDefaultTimerange() {
    if (this.defaultRange) {
      return;
    }
    if (!this.measure.hasTimerange) {
      return;
    }
    if (this.templateReport.predefinedTimeRange) {
      this.defaultRange = this.templateReport.predefinedTimeRange;
      return;
    }
    if (
      this.customReport.defaultRange &&
      this.customReport.defaultRange !== "custom"
    ) {
      this.defaultRange = this.customReport.defaultRange;
      return;
    }
    if (this.dates && this.dates.length > 2) {
      this.defaultRange = "custom";
      return;
    }
    const timeFilters = [
      "by_day",
      "by_week",
      "by_month",
      "by_quarter",
      "by_year",
    ];
    switch (this.dimension.code) {
      case "by_week":
        this.defaultRange = "30d";
        break;
      case "by_month":
        this.defaultRange = "6m";
        break;
      case "by_quarter":
        this.defaultRange = "1y";
        break;
      case "by_year":
        this.defaultRange = "3y";
        break;
      case "by_day":
        this.defaultRange = "7d";
        break;
      default:
        this.defaultRange = "30d";
        break;
    }
  }

  public async createCustomReport(keepName: boolean = true) {
    this.sendMixpanelEvent("create custom report");
    this.loadingSaveAs = true;
    if (!keepName) {
      this.newCustomReport.name = "Copy of " + this.newCustomReport.name;
    }
    // Generate current report query before saving
    this.newCustomReport.reportQuery = this.generateReportQuery();
    this.newCustomReport.dimension = this.dimension;
    this.newCustomReport.measure = this.measure;
    this.newCustomReport.analyticsType = this.analyticsType;
    this.newCustomReport.filterDimensions = this.filterDimensions;
    this.newCustomReport.filterColumns = this.filterColumns;
    this.newCustomReport.reportParams = this.reportParams;
    this.newCustomReport.defaultRange = this.defaultRange;
    this.newCustomReport.pivotOption = this.pivotOption;
    this.newCustomReport.reportColumns = this.selectedReportColumns;
    this.newCustomReport.filterTimerange = this.dates;
    this.newCustomReport.pageOptions = this.pageOptions;
    this.newCustomReport.productSegmentId = this.productSegment.id;
    this.newCustomReport.calculatedMetrics = this.calculatedMetrics;
    try {
      await this.newCustomReport.save();
      this.loadingSaveAs = false;
      this.dialogSaveAs = false;
      EventBus.$emit('show-snackbar', 'You have saved a new report');
      // Refresh count so canCanAddCustomReport reads up-to-date value
      this.refreshCustomReportsCount(true);
      // Review trigger fires via handleStandardLoad when the saved report page loads
      this.$router.push(`/my-reports/${this.newCustomReport.id}`);
    } catch (e: any) {
      this.loadingSaveAs = false;
      this.dialogSaveAs = false;
      EventBus.$emit('show-snackbar', { message: 'Error: ' + e.message, color: 'error'});
    }
  }

  public showCreateCustomReportMessage() {
    EventBus.$emit('show-snackbar', 'Please save as custom report first to use color rules');
  }

  public async saveCustomReport() {
    this.sendMixpanelEvent("update custom report");
    this.loadingSaveReport = true;
    // Generate current report query before saving
    this.customReport.reportQuery = this.generateReportQuery();
    this.customReport.dimension = this.dimension;
    this.customReport.measure = this.measure;
    this.customReport.analyticsType = this.analyticsType;
    this.customReport.filterDimensions = this.filterDimensions;
    this.customReport.filterColumns = this.filterColumns;
    this.customReport.reportParams = this.reportParams;
    this.customReport.defaultRange = this.defaultRange;
    this.customReport.filterTimerange = this.dates;
    this.customReport.pivotOption = this.pivotOption;
    this.customReport.reportColumns = this.selectedReportColumns;
    this.customReport.pageOptions = this.pageOptions;
    this.customReport.productSegmentId = this.productSegment.id;
    this.customReport.calculatedMetrics = this.calculatedMetrics;

    try {
      await this.customReport.update();
      this.loadingSaveReport = false;
      EventBus.$emit('show-snackbar', 'Your report is saved');
      // Flow C: Review ask after updating a custom report (recurring weekly)
      postUnlockReviewService.checkAndTriggerReview(this.store.id, 'favorite_reports_count');
    } catch (e: any) {
      this.loadingSaveReport = false;
      EventBus.$emit('show-snackbar', { message: 'Error: ' + e.message, color: 'error'});
    }
    this.dialogSaveAs = false;
  }

  public async renameCustomReport() {
    this.sendMixpanelEvent("rename custom report");
    this.loadingRename = true;
    this.reportName = this.customReport.name;
    try {
      this.customReport.name = this.reportName;
      await this.customReport.update();
      EventBus.$emit('show-snackbar', 'Your report is renamed');
      this.loadingRename = false;
      this.dialogRename = false;
    } catch (e: any) {
      this.loadingRename = false;
      this.dialogRename = false;
      EventBus.$emit('show-snackbar', { message: 'Error: ' + e.message, color: 'error'});
      //this.error(e);
    }
  }

  public async deleteCustomReport() {
    this.sendMixpanelEvent("delete custom report");
    this.loadingDelete = true;
    try {
      await this.customReport.del();
      this.loadingDelete = false;
      EventBus.$emit('show-snackbar', 'Your report is deleted');
      this.$router.push(`/my-reports`);
    } catch (e: any) {
      this.loadingDelete = false;
      EventBus.$emit('show-snackbar', { message: 'Error: ' + e.message, color: 'error'});
      //this.error(e);
    }
  }

  public notResetColumnFilter(e: any) {
    this.isResetFilterColumns = false;
  }

  public changeDimension(dimension: DimensionModel) {
    this.dimension = dimension;
  }

  public showFilterColumnMenu() {
    this.columnFilterMenu = true;
  }

  public closeColumnFilterMenu() {
    this.columnFilterMenu = false;
  }

  public showGroupByDimensionMenu() {
    this.groupByDimensionMenu = true;
  }

  public closeGroupByDimensionMenu() {
    this.groupByDimensionMenu = false;
  }

  public showDateRangeMenu() {
    this.dateRangeMenu = true;
  }

  public showReportGuideMenu() {
    this.reportGuideMenu = true;
  }

  public closeColumnSelectorMenu() {
    this.columnSelectorMenu = false;
  }

  public showColumnSelectorMenu() {
    this.columnSelectorMenu = true;
  }

  public closeReportGuideMenu() {
    this.reportGuideMenu = false;
  }

  public closeDateRangeMenu() {
    this.dateRangeMenu = false;
  }

  public showSaveAsFavoriteReportMenu() {
    this.dialogSaveAs = true;
  }

  public get canViewReport(): boolean {
    // Return false if measure data is not loaded yet
    if (!this.measure || !this.measure.code) {
      return true;
    }
    if (!this.$permissionService) {
      // Fallback or warning if service isn't available, deny permission
      return true; 
    } 
    // Check measure permission using PermissionService --- 
    const isPremiumMeasure = PREMIUM_MEASURE_CODES.includes(this.measure.code);
    
    if (isPremiumMeasure) {
      // Use the global $permissionService injected/provided
      if (!this.$permissionService.hasPermission(FEATURES.PAID_MEASURES)) {
        // If it's a premium measure and user lacks permission
        return false; // Permission denied for this measure
      }
    }

    // If it's not premium OR user has premium permission
    return true; 
  }

  public canCanAddCustomReport() {
    // Check base custom_reports permission first
    const customReportsUnlocked = this.store.isValidateRule('custom_reports', 'unlocked');
    console.log('[canCanAddCustomReport] custom_reports unlocked:', customReportsUnlocked);
    if (!customReportsUnlocked) {
      return false;
    }
    // Then check count limit
    const currentCount = this.customReports.size();
    const validateValues: any = [
      {
        code: "favorite_reports_count",
        value: currentCount + "",
      },
    ];
    const validateResult = this.store.validateRule(validateValues);
    console.log('[canCanAddCustomReport] favorite_reports_count check:', {
      currentCount,
      isValidate: validateResult.isValidate,
      rule: this.store.rules.items.find((r: any) => r.code === 'favorite_reports_count'),
    });
    return validateResult.isValidate;
  }

  public async refreshCustomReportsCount(force: boolean = false) {
    if (force || this.customReports.size() === 0) {
      try {
        this.customReports = new CustomReportList();
        await this.customReports.fetch();
      } catch (error) {
        console.error('[ReportPage] Failed to fetch custom reports count:', error);
      }
    }
  }

  public onFavoriteReportUnlocked() {
    window.location.reload();
  }

  public dimensionChanged(dimension: DimensionModel) {
    // Reset pinned columns if they don't exist in new dimension
    this.selectedReportColumns = [];
    this.savedReportColumns = [];
  }

  public updateReportOption(pageOptions: any) {
    this.pageOptions = pageOptions;
  }

  public updateReportColumn(data: any) {
    // --- Existing logic from the method ---
    if (!data.reportColumns || data.reportColumns.length === 0) {
      return;
    }
    if (data.reportColumnList && data.reportColumnList.length > 0) {
      this.initReportColumns = data.reportColumnList;
      this.reportColumns = data.reportColumnList;
    } else {
      this.initReportColumns = data.reportColumns;
      this.reportColumns = data.reportColumns;
    }
    this.reportDefaultColumns = data.reportColumns;
    this.initReportFilterColumns = this.reportColumns;
    this.addAllAutoSelectColumns();
    if (
      !this.selectedReportColumns ||
      this.selectedReportColumns.length === 0 ||
      this.isDimensionChanged
    ) {
      this.selectedReportColumns = this.reportDefaultColumns;
      this.savedReportColumns = this.reportDefaultColumns;
      this.isDimensionChanged = false;
    }
    if (this.initReportColumns && this.initReportColumns.length > 0) {
      if (!this.isCreated) {
        this.isCreated = true;
      }
    }
  }

  /**
   * Add a column to the selected report columns if it has autoSelect set to true and it is not a pivot column
   * @param column
   */
  public addAllAutoSelectColumns() {
    let newSelectedReportColumns: any = this.selectedReportColumns;
    //add all the autoSelect columns to the selected columns if it is not added yet
    for (const column of this.reportColumns) {
      if (column.autoSelect) {
        let isExist = false;
        for (const selectedColumn of newSelectedReportColumns) {
          if (selectedColumn.code === column.code) {
            isExist = true;
            break;
          }
        }
        if (!isExist) {
          newSelectedReportColumns.push(column);
        }
      }
    }
    this.selectedReportColumns = newSelectedReportColumns;
    this.savedReportColumns = newSelectedReportColumns;
  }

  public getPivotItems() {
    let pivots: any = [];
    if (this.pivotOption){
      try {
        pivots = this.initPivotColumnFromJson();
      } catch (error) {
        pivots = this.initPivotColumnFromString();
      }
    }
    return pivots;
  }

  public initPivotColumnFromString() {
    let pivots: any = [];
    if (this.pivotOption) {
      const pivotItems = this.pivotOption.split('_pivot_');
      for (const item of pivotItems) {
        const options = item.split('_cols_');
        if (options.length >= 2) {
          const columnCode: string = options[0]
            .replace('_rows_', '')
            .replace('+', ' ');
          const measureCode = options[1].replace('+', ' ');
          const columnValue = this.getReportColumnByCode(columnCode);
          const measureValue = this.getReportColumnByCode(measureCode);
          if (columnValue && measureValue) {
            pivots.push({
              column: columnValue,
              measure: measureValue,
              autoSelect: false,
            });
          }
        }
      }
    }
    return pivots;
  }

  public initPivotColumnFromJson() {
    let pivots: any = [];
    if (this.pivotOption) {
      const pivotItems = JSON.parse(this.pivotOption);
      for (const item of pivotItems) {
        const columnValue = this.getReportColumnByCode(item.column.code);
        const measureValue = this.getReportColumnByCode(item.measure.code);
        const autoSelect = item.autoSelect;
        if (columnValue && measureValue) {
          pivots.push({
            column: columnValue,
            measure: measureValue,
            autoSelect: autoSelect,
          });
        }
      }
    }
    return pivots;
  }

  public getReportColumnByCode(code: string) {
    for (const column of this.reportColumns) {
      if (column.code === code) {
        return column;
      }
    }
    for (const dimension of this.activeDimensions.items) {
      if (dimension.code === code) {
        return dimension;
      }
    }
    return null;
  }

  public saveReportHeaders(reportHeaders: any) {
    // build new list of columns
    const columns: any = [];
    for (const header of reportHeaders) {
      for (const c of this.reportColumns) {
        if (header.value === c.code) {
          const column = {
            name: header.text,
            code: c.code,
            dataType: c.dataType,
            luisMapping: c.luisMapping,
          };

          columns.push(column);
        }
      }
    }
    this.selectedReportColumns = columns;
    if (
      this.customReport &&
      this.customReport.id &&
      this.customReport.id !== undefined
    ) {
      this.customReport.reportColumns = columns;
      this.customReport.updateOptions();
    }
  }

  private sleep(ms: number) {
    return new Promise((resolve) => {
      setTimeout(resolve, ms);
    });
  }

  private rebuildSaveAsName() {
    let saveAsName = "";
    if (this.analyticsType.name) {
      saveAsName += this.analyticsType.name + " ";
    }
    saveAsName += this.measure.name;
    if (this.dimension.name) {
      saveAsName += " break-down by " + this.dimension.name;
    }
    this.newCustomReport.name = saveAsName;
  }

  private autoDetectTimerangeLimit() {
    this.timeRangeLimit = [];
    const dailyResources = [
      'locations',
      'tender_transactions',
      'customers',
      'smart_collections',
      'custom_collections',
      'collects',
      'product_metafields',
      'payouts',
      'transactions',
      'disputes',
      'variant_metafields',
      'suppliers',
      'purchase_orders',
      'stock_transfers',
      'mimoran_purchase_orders',
      'olap_historical_inventory_levels',
    ];
    if (this.measure.dataResources && this.measure.dataResources.length > 0) {
      for (const dataResource of this.measure.dataResources) {
        if (
          [
            'olap_current_inventory_levels',
            'inventory_level_quantities'
          ].includes(dataResource.code)
          && this.store.currentPackage.price === 0
        ) {
          dataResource.code = 'inventory_levels';
        }
        for (const etlStatus of this.store.etlStatuses.items) {
          if (dataResource.code === etlStatus.resource) {
            const lastUpdatedAt = etlStatus.lastUpdatedAt;
            const startTime = etlStatus.startTime;
            if (!isValidSyncDate(lastUpdatedAt) || !isValidSyncDate(startTime)) {
              continue;
            }
            let effectiveLastUpdated = lastUpdatedAt;
            let effectiveStart = startTime;
            if (this.timeRangeLimit.length > 0) {
              if (new Date(lastUpdatedAt) < new Date(this.timeRangeLimit[1])) {
                effectiveLastUpdated = this.timeRangeLimit[1];
              }
              if (new Date(startTime) > new Date(this.timeRangeLimit[0])) {
                effectiveStart = this.timeRangeLimit[0];
              }
            }
            this.timeRangeLimit = [
              effectiveStart,
              effectiveLastUpdated,
              dailyResources.includes(etlStatus.resource) ? 'daily' : 'hourly'
            ];
          }
        }
      }
    }
  }

  private getDatesFromDefaultTimeRange(timerange: string) {
    const now = moment();
    let startDate;
    let endDate;
    switch (timerange) {
      case "today":
        startDate = now;
        endDate = now;
        break;
      case "yesterday":
        const yesterday = now.clone().subtract(1, "days");
        startDate = yesterday;
        endDate = yesterday;
        break;
      case "7d":
        startDate = now.clone().subtract(7, "days");
        endDate = now.clone().subtract(1, "days");
        break;
      case "14d":
        startDate = now.clone().subtract(14, "days");
        endDate = now.clone().subtract(1, "days");
        break;
      case "3m":
        startDate = now.clone().subtract(3, "months");
        endDate = now.clone().subtract(1, "days");
        break;
      case "6m":
        startDate = now.clone().subtract(6, "months");
        endDate = now.clone().subtract(1, "days");
        break;
      case "1y":
        startDate = now.clone().subtract(1, "years");
        endDate = now.clone().subtract(1, "days");
        break;
      case "3y":
        startDate = now.clone().subtract(3, "years");
        endDate = now.clone().subtract(1, "days");
        break;
      case "30d":
        startDate = now.clone().subtract(30, "days");
        endDate = now.clone().subtract(1, "days");
        break;
      case "week-to-date":
        startDate = now.clone().startOf("week");
        endDate = now.clone().endOf("day");
        break;
      case "month-to-date":
        startDate = now.clone().startOf("month");
        endDate = now.clone().endOf("day");
        break;
      case "year-to-date":
        startDate = now.clone().startOf("year");
        endDate = now.clone().endOf("day");
        break;
      case "last-week":
        startDate = now.clone().subtract(1, "week").startOf("week");
        endDate = now.clone().subtract(1, "week").endOf("week");
        break;
      case "last-month":
        startDate = now.clone().subtract(1, "month").startOf("month");
        endDate = now.clone().subtract(1, "month").endOf("month");
        break;
      case "last-year":
        startDate = now.clone().subtract(1, "year").startOf("year");
        endDate = now.clone().subtract(1, "year").endOf("year");
        break;
      case "custom":
        startDate = new Date(this.dates[0]);
        endDate = new Date(this.dates[1]);
        break;
    }
    const range: Range = {
      start: startDate,
      end: endDate,
    };
    if (range.start && range.end) {
      if (timerange === "custom") {
        this.dates = [
          dateFormat(range.start, "yyyy-mm-dd"),
          dateFormat(range.end, "yyyy-mm-dd"),
          timerange,
        ];
      } else {
        this.dates = [
          dateFormat(range.start, "yyyy-mm-dd"),
          dateFormat(range.end, "yyyy-mm-dd"),
          this.defaultRange,
        ];
      }
    } else {
      this.dates = [];
    }
  }

  public generateReportQuery() {
    let reportQuery: any = this.prepareReportOptions();
    
    // Convert to JSON with UTF-8 encoding
    const jsonString = JSON.stringify(reportQuery);
    
    // Convert string to UTF-8 buffer
    const buffer = Buffer.from(jsonString, 'utf8');
    
    // Compress using zlib
    const compressed = zlib.deflateSync(buffer);
    
    // Convert to base64 and make URL safe
    const encodedString = compressed.toString('base64')
        .replace(/\+/g, '-')
        .replace(/\//g, '_')
        .replace(/=+$/, '');

    return encodedString;
  }

  public prepareReportOptions() {
    let reportQuery: any = {};
    reportQuery.title = this.reportName;
    reportQuery.chartType = this.chartType;
    reportQuery.hasChart = this.hasChart;
    reportQuery.displayMode = this.displayMode; // ADDED
    reportQuery.selectedCardColumns = this.getSelectedCardColumns;
    reportQuery.measure = this.measure.code;
    reportQuery.dimension = this.dimension.code;
    reportQuery.analyticsType = this.analyticsType.code;
    reportQuery.start_time = this.dates[0];
    reportQuery.end_time = this.dates[1];
    reportQuery.default_range = this.defaultRange;
    reportQuery.filter = this.filterColumns.items;
    reportQuery.pivot_option = this.pivotOption;
    reportQuery.params = this.reportParams.items;
    if (this.selectedReportColumns && this.selectedReportColumns.length > 0) {
      reportQuery.selected_report_columns = this.selectedReportColumns;
    } else {
      reportQuery.selected_report_columns = this.reportColumns;
    }
    reportQuery.page_options = this.pageOptions;
    reportQuery.product_segment_id = this.productSegment.id;
    reportQuery.calculated_metrics = this.calculatedMetrics.items;
    reportQuery.comparison_date = this.comparisonDateModel;
    // Add color rules to report query
    if (this.colorRules && this.colorRules.items.length > 0) {
      reportQuery.colorRules = this.colorRules.items.filter(rule => rule.measure === this.measure.code);
    }
    reportQuery.pinColumnCount = this.pinColumnCount;
    return reportQuery;
  }

  public async assignReportQuery(reportQuery: string = "") {
    let pinColumnCountWasLoaded = false; // Flag to track if loaded
    try {
        let reportQueryStr: any = reportQuery ? reportQuery : this.$route.query.report_query + '';
        if (reportQueryStr && reportQueryStr !== 'undefined') {
            // Restore base64 padding
            const m = reportQueryStr.length % 4;
            reportQueryStr = reportQueryStr
                .replace(/-/g, '+')
                .replace(/_/g, '/')
                .padEnd(reportQueryStr.length + (m === 0 ? 0 : 4 - m), '=');

            // Convert from base64 to buffer
            const buffer = Buffer.from(reportQueryStr, 'base64');
            
            // Decompress using zlib
            const decompressed = zlib.inflateSync(buffer);
            
            // Convert buffer to string using UTF-8 encoding
            const jsonString = decompressed.toString('utf8');
            
            // Parse the JSON
            const reportQueryObj = JSON.parse(jsonString);

            // Continue with existing code...
            if (reportQueryObj.measure) {
                this.measure = new MeasureModel();
                this.measure.code = reportQueryObj.measure;
                for (const measure of this.measures.items) {
                    if (measure.code === reportQueryObj.measure) {
                        this.measure = measure;
                    }
                }
            }

            // Restore all other properties
            for (const dimension of this.measure.dimensions.items) {
                if (dimension.code === reportQueryObj.dimension) {
                    this.dimension = dimension;
                }
            }

            for (const analyticsType of this.measure.analyticsTypes.items) {
                if (analyticsType.code === reportQueryObj.analyticsType) {
                    this.analyticsType = analyticsType;
                }
            }

            this.defaultRange = reportQueryObj.default_range;
            this.dates = [reportQueryObj.start_time, reportQueryObj.end_time, reportQueryObj.default_range];

            this.filterColumns = new FilterColumnList();
            if (reportQueryObj.filter && reportQueryObj.filter.length > 0) {
                this.filterColumns.mapData(reportQueryObj.filter);
            }
            this.initFilterColumns = this.filterColumns;
            this.reportFilters = this.initFilterColumns;

            if (reportQueryObj.pivot_option) {
                this.pivotOption = reportQueryObj.pivot_option;
                this.initPivotOption = this.pivotOption;
            }

            if (reportQueryObj.selected_report_columns && reportQueryObj.selected_report_columns.length > 0) {
                this.selectedReportColumns = reportQueryObj.selected_report_columns;
                this.savedReportColumns = reportQueryObj.selected_report_columns;
            }

            if (reportQueryObj.page_options) {
                this.pageOptions = reportQueryObj.page_options;
                this.initPageOptions = this.pageOptions;
            }

            if (reportQueryObj.product_segment_id) {
                this.productSegment.id = reportQueryObj.product_segment_id;
            }

            this.calculatedMetrics = new MetricList();
            if (reportQueryObj.calculated_metrics && reportQueryObj.calculated_metrics.length > 0) {
                this.calculatedMetrics.items = reportQueryObj.calculated_metrics;
            }
            this.initCalculatedMetrics = this.calculatedMetrics;

            if (reportQueryObj.comparison_date) {
                this.comparisonDateModel = reportQueryObj.comparison_date;
            }
            this.initComparisonDate = this.comparisonDateModel;

            if (reportQueryObj.title) {
                this.reportName = reportQueryObj.title;
            }
            document.title = this.reportName;

            if (reportQueryObj.chartType) {
                this.chartType = reportQueryObj.chartType;
            }
            if (reportQueryObj.hasChart !== undefined) {
                this.hasChart = reportQueryObj.hasChart;
            }
            if (reportQueryObj.displayMode !== undefined) {
               this.displayMode = reportQueryObj.displayMode;
            } else {
               // Fallback if older saved report query doesn't have displayMode
               this.displayMode = (reportQueryObj.showChart !== undefined && reportQueryObj.showChart) ? 'visualization' : 'table';
            }
            if (reportQueryObj.selectedCardColumns) {
                this.selectedCardColumns = reportQueryObj.selectedCardColumns;
            }
            if (reportQueryObj.colorRules) {
                this.colorRules = new ColorRuleList();
                this.colorRules.mapData(reportQueryObj.colorRules);
            }
            if (reportQueryObj.pinColumnCount !== undefined) {
                this.pinColumnCount = reportQueryObj.pinColumnCount;
            }
            this.adjustPinColumnCountForScreenSize();
            
            await this.initAvailableParams();
            this.reportParams = new ParamList();
            if (reportQueryObj.params && reportQueryObj.params.length > 0) {
              this.reportParams.mapData(reportQueryObj.params);
              this.loadReportParamFromSavedParams(this.reportParams);
              this.initReportParams = this.reportParams;
              this.isInitReportParamFromParam = true;
            } else {
              this.reportParams = new ParamList();
              this.initReportParams = this.reportParams;
            }

        }
    } catch(e: any) {
        // Handle error appropriately
    }
  }

  public isReportQueryChange() {
    const currentReportQuery = this.generateReportQuery();
    //now get the reportQuery from the url
    const reportQuery = this.$route.query.report_query;
    if (reportQuery === currentReportQuery) {
      return false;
    }
    return true;
  }

  public resetReportQuery() {
    this.dates = [];
    this.templateReport = new TemplateReportModel();
    this.customReport = new CustomReportModel();
    this.filterDimensions = new FilterDimensionList();
    this.filterColumns = new FilterColumnList();
    this.initFilterColumns = new FilterColumnList();
    this.reportFilters = this.initFilterColumns;
    this.reportParams = new ParamList();
    this.initReportParams = new ParamList();
    this.reportColumns = [];
    this.selectedReportColumns = [];
    this.savedReportColumns = [];
    this.measure = new MeasureModel();
    this.dimension = new DimensionModel();
    this.analyticsType = new AnalyticsTypeModel();
    this.pivotOption = "";
    this.initPivotOption = "";
    this.colorRules = new ColorRuleList();
  }

  public replaceReportQuery() {
    try {
      if (this.isReportQueryChange()) {
        this.$router.replace({
          query: {
            report_query: this.generateReportQuery(),
          },
        });
      }
    } catch(e: any) {
      // ignore this catch
    }
  }

  @Watch("$route", { immediate: true, deep: true })
  private async onUrlChange(newVal: any) {
    if (!this.readyToGetReport) {
      return;
    }
    if (this.isReportQueryChange()) {
      this.resetReportQuery();
      await this.fullLoadData();
      this.initializeDrawerState();
    }
  }

  @Watch("productSegment", { immediate: true, deep: true })
  private onProductSegmentChanged(newVal: any, oldVal: any) {
    if (!this.readyToGetReport) {
      return;
    }

    if (!oldVal && !newVal) {
      return;
    }
    this.replaceReportQuery();
  }

  @Watch("filterColumns", { immediate: true, deep: true })
  private onFilterColumnsChanged(newVal: any, oldVal: any) {
    if (!this.readyToGetReport) {
      return;
    }
    if (!oldVal && !newVal) {
      return;
    }
    this.replaceReportQuery();
  }

  @Watch("dates", { immediate: true, deep: true })
  private async onDatesChanged(newVal: any) {
    if (this.dates.length < 3) {
      return;
    }
    this.defaultRange = this.dates[2];
    if (this.readyToGetReport) {
      this.replaceReportQuery();
    }
  }

  @Watch("reportParams", { immediate: true, deep: true })
  private onReportParamsChanged(newVal: any) {
    if (this.reportParams && this.reportParams.items.length > 0 && this.readyToGetReport) {
      this.replaceReportQuery();
    }
  }

  @Watch("pageOptions", { immediate: true, deep: true })
  private onPageOptionsChanged(newVal: any) {
    if (this.pageOptions && this.pageOptions.sortBy && this.readyToGetReport) {
      this.replaceReportQuery();
    }
  }

  @Watch("pivotOption", { immediate: true, deep: true })
  private onPivotOptionChanged(newVal: any) {
    if (this.pivotOption && this.readyToGetReport) {
      this.replaceReportQuery();
    }
  }

  @Watch("comparisonDateModel", { immediate: true, deep: true })
  private onComparisonDateModelChanged(newVal: any) {
    if (this.comparisonDateModel && this.comparisonDateModel.code && this.readyToGetReport) {
      this.replaceReportQuery();
    }
  }

  @Watch("calculatedMetrics", { immediate: true, deep: true })
  private onCalculatedMetricsChanged(newVal: any) {
    if (!this.readyToGetReport) {
      return;
    }
    if (this.calculatedMetrics && this.calculatedMetrics.items.length > 0) {
      this.replaceReportQuery();
    }
  }

  @Watch("selectedReportColumns", { immediate: true, deep: true })
  private onSelectedReportColumnsChanged(newVal: any) {
    if (this.selectedReportColumns && this.selectedReportColumns.length > 0 && this.readyToGetReport) {
      const columns: any = [];
      for (const c of this.selectedReportColumns) {
        columns.push(c);
      }
      this.savedReportColumns = columns;
      this.sendMixpanelEvent("add or remove report column");
      if (this.readyToGetReport) {
        this.replaceReportQuery();
      }
    }
  }

  @Watch("initDimension", { immediate: true, deep: true })
  private async onDimensionChanged(newVal: any) {
    if (this.dimension && this.dimension.code && this.initDimension && this.initDimension.code && this.initDimension.code !== this.dimension.code && this.readyToGetReport) {
      this.dimension = this.initDimension;
      this.initReportParams = new ParamList();
      this.reportParams = new ParamList();
      this.selectedReportColumns = [];
      this.savedReportColumns = [];
      this.isDimensionChanged = true;
      await this.initAvailableParams();
      this.initParamFromDefaultValue();
      //this.replaceReportQuery();
    }
  }

  @Watch("reportFilters", { deep: true })
  private onReportFiltersChanged() {
    if (!this.readyToGetReport) {
      return;
    }
    if (this.reportFilters && this.reportFilters.items.length > 0) {
      this.filterColumns = new FilterColumnList();
      for (const filter of this.reportFilters.items) {
        this.filterColumns.add(filter);
      }
    } else {
      this.filterColumns = new FilterColumnList();
    }
    this.needRefreshTableData = true;
    this.needRefreshMetricData = true;
  }

  @Watch("searchData", { deep: true })
  private onSearchDataChange() {
    if (!this.readyToGetReport) {
      return;
    }
    this.filterColumns = new FilterColumnList();
    if (this.reportFilters && this.reportFilters.items.length > 0) {
      for (const item of this.reportFilters.items) {
        this.filterColumns.add(item);
      }
    }
    // Only add searchData if it's not null and has valid values
    if (this.searchData && this.searchData.value && this.searchData.value.length > 0) {
      this.filterColumns.add(this.searchData);
    }
    this.needRefreshMetricData = true;
    this.needRefreshTableData = true;
  }

  public showColorRuleEditor() {
    this.showColorRuleEditorDialog = true;
  }

  public saveColorRules(rules: ColorRule[]) {
    this.colorRules = new ColorRuleList();
    this.colorRules.mapData(rules);
    this.showColorRuleEditorDialog = false;
    this.needRefreshTableData = true;
    // Save custom report after color rules are updated
    if (this.customReport.id) {
      this.saveCustomReport();
    }
    this.replaceReportQuery();
  }

  private toggleSection(section: 'filters' | 'params' | 'settings' | 'actions' | 'customize') {
    this.expandedSections[section] = !this.expandedSections[section];
  }

  public handlePinColumnUpdate(data: any  ) {
    this.pinColumnCount = data.pinColumnCount;
    this.selectedReportColumns = data.updatedColumns;
    this.savedReportColumns = data.updatedColumns;
    this.replaceReportQuery();
  }

  public handleTableLoadingState(isLoading: boolean) {
    this.isTableReportLoading = isLoading;
  }

  public get isPageReady(): boolean {
    return !this.loading && this.finalPermissionCheckComplete;
  }

  public get showSetupReportButton(): boolean {
    return this.canViewReport && !this.showLockInventoryOptimization && !this.fullscreenDataTable;
  }

  get isReplenishmentReport(): boolean {
    return this.settingPages.some((page: any) => page.measure === this.measure.code);
  }

  private refreshReportAfterSync(): void {
    this.needRefreshTableDataEvent();
    this.needRefreshMetricDataEvent();
  }

  public get mainContentClass(): string {
    // If we're in fullscreen mode, use the fullscreen class
    if (this.fullscreenDataTable) {
      return 'report-page-datatable-size-big-fullscreen';
    }
    // Otherwise adjust width based on drawer state
    return this.drawerOpen ? 'content-with-drawer' : 'content-full-width';
  }

  public get sortableColumns(): SortableColumn[] {
    return this.reportColumns.filter((col: any) => col.sortable === true);
  }

  public onSortChanged(event: { sortBy: string[], sortDesc: boolean[] }) {
    this.pageOptions.sortBy = event.sortBy;
    this.pageOptions.sortDesc = event.sortDesc;

    // Trigger table update by creating new object reference
    this.pageOptions = { ...this.pageOptions };

    // Explicitly trigger report reload
    this.replaceReportQuery();
    this.needRefreshTableData = true;
  }

  public toggleDrawer(): void {
    this.drawerOpen = !this.drawerOpen;
    localStorage.setItem('drawerOpen', this.drawerOpen.toString()); 
    EventBus.$emit('report-setup-open', this.drawerOpen);
    this.sendMixpanelEvent(this.drawerOpen ? "open setup drawer" : "close setup drawer");
  }

  // ADDED Watcher for displayMode
  @Watch("displayMode")
  private onDisplayModeChanged() {
    if (!this.isCreated) return; // Only run after component is created

    this.isSwitchingView = true;

    this.$nextTick(() => {
      setTimeout(() => {
        this.isSwitchingView = false;
        if (this.readyToGetReport) {
          // Update URL when view mode changes and switch is complete
          this.replaceReportQuery();
        }
      }, 100); // Short delay to allow rendering and cover perceived lag
    });
  }

  public adjustPinColumnCountForScreenSize() {
    // Set initial pin column count based on screen size
    if (this.$vuetify.breakpoint.mdAndDown) {
      this.pinColumnCount = 2; 
    } else if (this.$vuetify.breakpoint.smAndDown) {
      this.pinColumnCount = 1;
    }
  }

  // --- START Watcher for tableViewMode --- 
  @Watch("tableViewMode")
  private onTableViewModeChanged(newValue: string) {
    localStorage.setItem('reportTableViewMode', newValue);
  }
  // --- END Watcher for tableViewMode --- 

  private openInteractiveGuide(): void {
    window.open('https://app.arcade.software/share/cALrT4m06IERHaqtsnvv', '_blank');
  }

  // Report Assistant Dialog Methods
  public get sortColumn(): any {
    // Return current sort configuration from pageOptions
    return {
      column: this.pageOptions.sortBy || null,
      direction: this.pageOptions.sortDesc ? 'desc' : 'asc'
    };
  }

  public onAssistantFiltersChanged(filters: any[], logic: string): void {
    // Store the filters from assistant
    this.assistantFilters = filters;
    
    // Convert assistant filters to FilterColumnModel format and update filterColumns
    if (filters && filters.length > 0) {
      const filterColumnModels = filters.map((filter: any) => {
        const filterModel = new FilterColumnModel();
        filterModel.name = filter.name;
        filterModel.code = filter.code;
        filterModel.dataType = filter.dataType;
        filterModel.operator = filter.operator;
        filterModel.value = filter.value;
        // Store logic separately as it's not a property of FilterColumnModel
        return filterModel;
      });
      
      // Create new FilterColumnList and add filters
      const filterList = new FilterColumnList();
      filterColumnModels.forEach(model => filterList.add(model));
      
      // Update filterColumns which is bound to the filter-column component
      this.filterColumns = filterList;
      this.initFilterColumns = filterList;
      // Also update reportFilters
      const reportFilterList = new FilterColumnList();
      filterColumnModels.forEach(model => reportFilterList.add(model));
      this.reportFilters = reportFilterList;

    } else {
      // Clear filters if empty
      this.filterColumns = new FilterColumnList();
      this.reportFilters = new FilterColumnList();
    }
  }

  public onAssistantSortChanged(sort: any): void {
    // Store the sort from assistant
    this.assistantSort = sort;
    
    // Apply sort to pageOptions which controls table sorting
    if (sort && sort.column) {
      this.pageOptions.sortBy = [sort.column];
      this.pageOptions.sortDesc = [sort.direction === 'desc'];
      
      // Trigger table update
      this.pageOptions = { ...this.pageOptions };
    }
    
    this.replaceReportQuery();
    this.needRefreshTableData = true;
    

  }

  public onAssistantLimitChanged(limit: number): void {
    // Apply limit to pageOptions which controls pagination
    if (limit && limit > 0) {
      this.pageOptions.page = 1;  // Reset to first page when changing limit
      this.pageOptions.itemsPerPage = limit;
      
      // Trigger table update
      this.pageOptions = { ...this.pageOptions };
    }
    this.replaceReportQuery();
    this.needRefreshTableData = true;
  }

  public onAssistantTimeRangeChanged(timeRange: any): void {
    // Handle time range changes from assistant
    if (!timeRange) {
      console.error('No time range provided');
      return;
    }
    // Update the defaultRange with the period from time range
    if (timeRange.period) {
      this.defaultRange = timeRange.period;
      let dateRanges: string[] = [timeRange.from, timeRange.to, this.defaultRange];
      this.dates = dateRanges;
    }
  }

  public onAssistantDimensionChanged(dimensionCode: string): void {
    // Find the dimension object from measure's available dimensions
    
    if (!dimensionCode || !this.measure || !this.measure.dimensions) {
      console.error('Cannot change dimension: missing required data');
      return;
    }
    
    // Find the dimension in the measure's dimensions
    const newDimension = this.measure.dimensions.items.find(
      (dim: any) => dim.code === dimensionCode || dim.luis_mapping === dimensionCode
    );
    
    if (newDimension) {
      
      // Change the dimension using the existing method
      this.changeDimension(newDimension);
      
      // Refresh the report data with new dimension
      this.replaceReportQuery();
      this.needRefreshTableData = true;
      
      // Close the assistant dialog after dimension change
      setTimeout(() => {
        this.showAssistantDialog = false;
      }, 500);
    }
  }

  public onAssistantColumnsChanged(columnsData: any): void {
    // Store the columns configuration from assistant
    this.assistantColumns = columnsData.columns || [];
    
    // Handle column operations for selectedReportColumns
    if (columnsData.operation === 'add') {
      // Add columns to selected columns
      const currentCodes = this.selectedReportColumns.map((c: any) => c.code);
      const newColumns = columnsData.columns.filter(
        (c: any) => !currentCodes.includes(c.code)
      );
      this.selectedReportColumns = [...this.selectedReportColumns, ...newColumns];
      
    } else if (columnsData.operation === 'remove') {
      // Remove columns from selected columns
      const columnsToRemove = columnsData.columns.map((c: any) => c.code);
      
      
      const originalLength = this.selectedReportColumns.length;
      this.selectedReportColumns = this.selectedReportColumns.filter(
        (col: any) => !columnsToRemove.includes(col.code)
      );
      
    } else if (columnsData.operation === 'only') {
      // Show only specified columns
      this.selectedReportColumns = columnsData.columns;
    }
    
    // Also update selectedCardColumns if it exists
    if (this.selectedCardColumns) {
      this.selectedCardColumns = [...this.selectedReportColumns];
    }

    this.savedReportColumns = [...this.selectedReportColumns];
    
    this.replaceReportQuery();
    this.needRefreshTableData = true;
  }

  public onAssistantReportConfigUpdated(config: any): void {
    // Handle complete configuration update from assistant
    if (config.filters !== undefined) {
      this.onAssistantFiltersChanged(config.filters, config.filterLogic || 'AND');
    }
    
    if (config.sort !== undefined) {
      this.onAssistantSortChanged(config.sort);
    }
    
    if (config.columns !== undefined) {
      this.onAssistantColumnsChanged({ 
        operation: 'replace', 
        columns: config.columns 
      });
    }
    
    if (config.limit !== undefined) {
      this.onAssistantLimitChanged(config.limit);
    }
    
    if (config.dimension !== undefined) {
      this.onAssistantDimensionChanged(config.dimension);
    }
    
    // Automatically apply changes and refresh the report
    this.getReportData();
  }

  public applyAssistantChanges(): void {
    // Apply all changes and close dialog
    this.showAssistantDialog = false;
    
    // Trigger data refresh to apply filters and sorting
    this.getReportData();
  }
}
